// buildui.ts ↔ the in-game modal command UI — the forms that complete the
// build/command gameplay loop:
//   - UnitMenu + UnitGob::PopupMenu/LoadMenu + GetPrerequisiteString
//     (game/Unit.cpp:30-60, 707-748, 1079-1350)
//   - the per-gob menu dispatch the C++ does virtually: Gob/StructGob/HqGob/
//     MobileUnitBuilderGob/MinerGob/MobileHqGob InitMenu + OnMenuItemSelected
//     (game/GameObjects.cpp:510-522, game/Struct.cpp:797-825,
//     game/Headquarters.cpp:123-173, game/MobileBuilder.cpp:38-60,
//     game/Miner.cpp:168-195, game/MobileHQ.cpp:109-136)
//   - StructGob's recycle confirmation (game/Struct.cpp:246-340)
//   - StructureBuildForm + PlaceStructureForm (game/Headquarters.cpp:5-24,
//     315-1132) — the HQ structure flow ending in the EXACT
//     kmidBuildOtherCommand enqueue
//   - MobileUnitBuildForm + BuildListControl + BuilderGob::DrawBuildProgress
//     (game/Builder.cpp:417-426, 489-1066) — the VTS/HRC unit-order flow
//   - GobMgr::IsStructurePlacementValid (game/GameObjects.cpp:2222-2298 — a
//     GobMgr method in the C++, but it reads gpmfrmm, so it is client UI)
//
// The C++ statics (HqGob::s_pfrmBuild, gpfrmPlace, VtsGob/HrcGob::s_pfrmBuild,
// StructGob::s_pfrmConfirmation, gapuntc[ut]->pfrmMenu, the placement tile
// bitmaps and the per-class sfx assignments from the InitClass'es) live on the
// BuildUI bundle a GameSession constructs. DoModal is continuation-shaped (see
// form.ts) so every C++ `DoModal(&idc); <act on idc>` sequence becomes
// DoModal(pnResult, ..., () => { <act on pnResult.v> }).

import type { CpuDibBitmap } from '../gfx/dibbitmap.ts';
import { CpuDibBitmap as CpuDib } from '../gfx/dibbitmap.ts';
import type { TBitmap } from '../gfx/tbitmap.ts';
import type { UpdateMap } from '../gfx/updatemap.ts';
import { GetColor, GetSideColor, iclr } from '../gfx/palette.ts';
import type { Event } from './event.ts';
import {
  penDownEvent,
  penMoveEvent,
  penUpEvent,
  penDownEvent2,
  penMoveEvent2,
  penUpEvent2,
  kfEvtFinger,
  kfRedrawDirty,
  kfRedrawBeforeTimer,
} from './event.ts';
import { Rect, type RectLike } from './rect.ts';
import {
  ShadowHelper,
  DrawBorder,
  PcFromFc,
  DrawFancyText,
  kidcOk,
  kidcCancel,
  ksfxGuiFormShow,
  ksfxGuiFormHide,
  ksfxGuiButtonTap,
  kfCtlVisible,
  kfCtlDisabled,
  kfFrmVisible,
  kfFrmTranslucent,
} from './uikit.ts';
// PlaceStructureForm/UnitTitleLayer are PaintLayers driven by the renderer-owned
// paint engine (canvas-removal R3). The build/recruit/research/place/menu forms
// are model-builders, not Form control-hosts. DrawFancyText (the title-label
// render) moved to uikit.ts; the pip-meter glyph width is loaded directly via
// host.createTBitmap (the forms.ini control set — misccontrols.ts — is deleted).
import type { PaintLayer, FormMgr, MultiFormMgr } from '../render/paintengine.ts';
import { ksideNeutral } from '../gfx/tbitmap.ts';
import type { FlickScroller } from './flickscroller.ts';
import { CallbackTimer, TimerMgr } from './timer.ts';
import type { SimUIForm as SimUIFormInput } from './fingerhandler.ts';
import type { GobDrawContext } from '../render/gobdraw.ts';
import type { SimContext } from '../../../sim/src/context.ts';
import { IniReader, IniScanf } from '../../../sim/src/data/ini.ts';
// forms.ini-elimination (Phase 4): the in-game build/recruit/research/place/unit-menu
// Forms Init against this in-code copy of the forms.ini sections, NOT the shipped forms.ini
// in the pdb — so buildui's in-game UI is no longer a forms.ini Form. The parse/scale code
// path is the SAME, so the control trees are byte-identical (build-forms-geometry.test.ts).
import { kBuildFormSections } from './build-forms-geometry.ts';
import {
  newMessage,
  ksmidNull,
  kgidNull,
  kmidBuildOtherCommand,
  kmidAbortBuildOtherCommand,
  kmidRepairCommand,
  kmidSelfDestructCommand,
  kmidTransformCommand,
  kmidDeliverCommand,
  kmidUpgradeCommand,
  kmidAbortUpgradeCommand,
  kfUpgradeHrc,
  kfUpgradeVts,
  kfUpgradeHrcInProgress,
  kfUpgradeVtsInProgress,
} from '../../../sim/src/sm/message.ts';
import type { Gob } from '../../../sim/src/gob/gob.ts';
import {
  kfGobUnit,
  kfGobStructure,
  kfGobActive,
  kfGobBeingBuilt,
} from '../../../sim/src/gob/gob.ts';
import type { UnitGob } from '../../../sim/src/gob/unitgob.ts';
import { GetUnitConsts as SimGetUnitConsts } from '../../../sim/src/gob/unitconsts.ts';
import {
  UnitConsts,
  StructConsts,
  BuilderConsts,
  MobileUnitConsts,
  kwcTile,
  kwcTileHalf,
} from '../../../sim/src/gob/unitconsts.ts';
import { StructGob } from '../../../sim/src/gob/struct.ts';
import { BuilderGob, BuildQueue, MobileUnitBuilderGob } from '../../../sim/src/gob/builder.ts';
import { HqGob } from '../../../sim/src/gob/hq.ts';
import { MinerGob } from '../../../sim/src/gob/miner.ts';
import { MobileHqGob } from '../../../sim/src/gob/mobilehq.ts';
import { ResearchGob } from '../../../sim/src/gob/research.ts';
import { IsTileFree } from '../../../sim/src/gob/tilesearch.ts';
import { kgtMachineGunTower, kgtRocketTower } from '../../../sim/src/gob/gobtypes.ts';
import { kgtHeadquarters } from '../../../sim/src/gob/gobtypes.ts';
import {
  kutMax,
  kutVehicleTransportStation,
  kutHumanResourceCenter,
} from '../../../sim/src/player/side.ts';
import {
  kumStructures,
  kumTowers,
  kumAll,
  kfUnitRepairing,
} from '../../../sim/src/player/masks.ts';
import { knLimitStruct, knLimitMobileUnit } from '../../../sim/src/gob/gobmgr.ts';
import type { Player } from '../../../sim/src/player/player.ts';
import { kbfStructure, kbfReserved } from '../../../sim/src/terrain/terrainmap.ts';
import { TcFromWc, WcFromTc, WcFromTile16ths } from '../../../sim/src/math/coords.ts';
import { itofx, fxtoi, addfx, subfx, divfx, mulfx } from '../../../sim/src/math/fix.ts';

// game/res.h — form ids
// (kidfAreYouSure 1001 — the confirmation form — was canvas-only; deleted with the canvas
//  PopupConfirmation arm in canvas-removal R2. The DOM confirm-view.ts replicates it.)
export const kidfBuildInfantry = 1011;
export const kidfStructMenu = 1013;
export const kidfBuildVehicle = 1015;
export const kidfBuildStructure = 1017;
export const kidfPlaceStructure = 1018;
export const kidfUnitMenu = 1030;

// game/res.h — UnitMenu control ids
export const kidcTitle = 1200;
export const kidcCantTransform = 1201;
export const kidcRelocButtonMin = 1300;
export const kidcRepair = 1300;
export const kidcSelfDestruct = 1301;
export const kidcAbortBuild = 1302;
export const kidcAbortRepair = 1303;
export const kidcBuild = 1304;
export const kidcAbortUpgrade = 1304;
export const kidcResearch = 1305;
export const kidcTransform = 1306;
export const kidcDeliver = 1306;
export const kidcRelocButtonMax = 1307;

// game/res.h — build form control ids
export const kidcName = 1001;
export const kidcCost = 1002;
export const kidcMoveRate = 1003;
export const kidcMoveRateMeter = 1103;
export const kidcArmorStrength = 1004;
export const kidcArmorStrengthMeter = 1104;
export const kidcWeaponStrengthMeter = 1105;
export const kidcWeaponRange = 1006;
export const kidcWeaponRangeMeter = 1106;
export const kidcList = 1007;
export const kidcOrder = 1008;
export const kidcCancelOrder = 1009;
export const kidcDescription = 1010;
export const kidcCostMeter = 1101;
export const kidcLimitReached = 1200;
export const kidcPowerSupply = 1011;
export const kidcPowerSupplyMeter = 1111;
export const kidcPowerDemand = 1012;
export const kidcPowerDemandMeter = 1112;
export const kidcHelp = 2004;

// game/res.h:76-78
const kifntTitle = 3;

// game/ht.h:6565
const kcyHealthBar = 4;

// game/ht.h:1019
const kctMapScrollRate = 8;

// game/ht.h:766-773 — the web build's scroll tuning (kwcScrollLeftExtra is
// IPHONE-only)
const kwcScrollBorderSize = WcFromTile16ths(5);
const kwcScrollStepSize = kwcTileHalf;

// game/Builder.cpp:5
const kcyBuildProgress = 3;

// game/soundeffects.h enum Sfx (order-derived indices)
export const ksfxGalaxMinerDeliver = 16;
export const ksfxHeadquartersAbortConstruction = 36;
export const ksfxHeadquartersAbortRepair = 37;
export const ksfxHeadquartersConstruct = 38;
export const ksfxHeadquartersDamaged = 39;
export const ksfxHeadquartersDestroyed = 40;
export const ksfxHeadquartersRepair = 41;
export const ksfxHeadquartersSelect = 42;
export const ksfxHeadquartersStructureReady = 43;
export const ksfxHumanResourceCenterAbortRecruiting = 44;
export const ksfxHumanResourceCenterAbortRepair = 45;
export const ksfxHumanResourceCenterDamaged = 46;
export const ksfxHumanResourceCenterDestroyed = 47;
export const ksfxHumanResourceCenterRecruit = 48;
export const ksfxHumanResourceCenterRepair = 49;
export const ksfxHumanResourceCenterSelect = 50;
export const ksfxHumanResourceCenterUnitReady = 51;
export const ksfxVehicleTransportStationAbortManufacture = 95;
export const ksfxVehicleTransportStationAbortRepair = 96;
export const ksfxVehicleTransportStationDamaged = 97;
export const ksfxVehicleTransportStationDestroyed = 98;
export const ksfxVehicleTransportStationManufacture = 99;
export const ksfxVehicleTransportStationRepair = 100;
export const ksfxVehicleTransportStationSelect = 101;
export const ksfxVehicleTransportStationVehicleReady = 102;

/** UnitTypeFromPVoid — the C++ stores ut as the ListItem's void* data (ledger-tracked
 *  port symbol; the R3 model-builders inlined its use, so it survives as the exported
 *  helper for any remaining pv->ut readers). */
export const UnitTypeFromPVoid = (pv: unknown): number => pv as number;

export const kidfUpgrade = 1045; // res.h:42
// res.h:380-382 — the Upgrade form's info labels
export const kidcPrerequisites = 1201;
export const kidcPrerequisitesLabel = 1202;
export const kidcCostLabel = 1203;

// ht.h:5603-5608
export const kupgmAdvancedHRC = 1 << 0;
export const kupgmAdvancedVTS = 1 << 1;

/** game/Research.cpp:36-91 — the SP/MP Upgrade display tables (ht.h Upgrade).
 *  ctTimeToBuild (4500) is unused by the form. */
export interface Upgrade {
  upgm: number;
  szName: string;
  szIconName: string;
  upgmPrerequisites: number;
  umPrerequisites: number;
  nCost: number;
  szDescription: string;
  szPrerequisites: string;
}
const kumHumanResourceCenter = (1 << kutHumanResourceCenter) >>> 0;
const kumVehicleTransportStation = (1 << kutVehicleTransportStation) >>> 0;
export const gaupg: Upgrade[] = [
  {
    upgm: kupgmAdvancedHRC,
    szName: 'Advanced HRC',
    szIconName: 'hrc1',
    upgmPrerequisites: 0,
    umPrerequisites: kumHumanResourceCenter,
    nCost: 750,
    szDescription: 'Upgrade the HRC to gain access to more advanced infantry.',
    szPrerequisites: 'This upgrade requires a HRC.',
  },
  {
    upgm: kupgmAdvancedVTS,
    szName: 'Advanced VTS',
    szIconName: 'vts1',
    upgmPrerequisites: 0,
    umPrerequisites: kumVehicleTransportStation,
    nCost: 1150,
    szDescription: 'Upgrade the VTS to gain access to more advanced vehicles.',
    szPrerequisites: 'This upgrade requires a VTS.',
  },
];
export const gaupgMP: Upgrade[] = [
  { ...gaupg[0], nCost: 1500 },
  { ...gaupg[1], nCost: 3000 },
];
const gaupgNames = gaupg.map((u) => u.szName); // kupgtMax = 2 (ht.h:5603)

/** The injected C++ globals the build UI reaches. */
export interface BuildUIDeps {
  /** gsim/ggobm/gpplrLocal/gfGodMode/gcmdq reach. */
  ctx: SimContext;
  /** the input slice — gsim.GetViewPos/SetViewPos's scroll plumbing. */
  sui: SimUIFormInput;
  /** the render context (coords + panid icons). */
  gctx: GobDrawContext;
  gtimm: TimerMgr;
  /** gpmfrmm. */
  mfrmm: MultiFormMgr;
  /** gpfrmmSim. */
  gpfrmmSim: FormMgr;
  /** gpiniGobs (GobTemplates.ini — UnitGob::LoadMenu's "Menu" override). */
  templatesIni: IniReader;
  /** ggame.GetPlayfieldSize. */
  GetPlayfieldSize(): { cx: number; cy: number };
  /** gsim.GetLevel()->GetTileMap()->GetMapSize (PIXELS). */
  GetMapSizePx(): { cx: number; cy: number };
  /** HTML/DOM UI shell plan, Phase 2 (forms.ini elimination): the minimap's on-screen
   *  RECT in back-buffer px (the canvas pfrmMiniMap->GetRect the C++ read). The canvas
   *  minimap FORM is gone, so the host computes it from the shared minimap geometry
   *  (miniMapScreenRect — the SAME rect the DOM radar floats over). Used ONLY for the
   *  structure-placement DEAD ZONE: structures can't be placed under the radar's screen
   *  corner (PlaceUI / CanPlaceStruct). Null ⇒ no radar (the dead zone is skipped). */
  getMiniMapRect(): RectLike | null;
  /** punt->GetAnimSprite()->GetBounds() (Unit.cpp:1180-1185) — the hilight
   *  sprite is host presentation; null = no sprite (the C++ NULL arm). */
  GetAnimSpriteBounds(punt: UnitGob): RectLike | null;
  /** gsndm.PlaySfx. */
  PlaySfx(sfx: number): void;
  /** gevm.SetRedrawFlags (PlaceStructureForm::OnTimer). */
  SetRedrawFlags(wf: number): void;
  /** m_flics construction for list controls (gevm + the host clock). */
  newFlickScroller(): FlickScroller;
  /** Phase 5 — build/order STEP 1: the kidfAreYouSure confirmation as a DOM overlay (the
   *  canvas PopupConfirmation LoadForm+DoModal arm was deleted in canvas-removal R2).
   *  PopupConfirmation calls this and routes its result to the SAME continuation (YES runs
   *  the existing command-
   *  enqueueing action; NO does nothing). The view emits NO command/Message — the
   *  command stays here. `titleColor` is the kidcTitle side color (GetSideColor) as a
   *  CSS string. Absent (a headless harness) ⇒ no UI shown. DISPLAY-only. */
  domConfirm?(
    title: string,
    titleColor: string,
    anchor: { cx: number; cy: number } | null,
    onResult: (confirmed: boolean) => void,
  ): void;
  /** Phase 5: the SECTION-ONLY in-game HELP overlay.
   *  When set, a build panel's "?" (kidcHelp) calls this with the panel's context section
   *  anchor INSTEAD of the canvas Help() manual: structure → "buildings", VTS-unit →
   *  "vehicles", HRC-unit → "personnel", research → "upgrades" (mirroring the C++
   *  Help(m_idf == kidfBuildInfantry ? "personnel" : "vehicles", …) — Builder.cpp:703). The
   *  host mounts a HelpView OVER the build panel (it stays open underneath); the view fetches
   *  /help.txt, extracts that section, and renders it. DISPLAY-only — no command/Message.
   *  Absent (a headless harness) ⇒ no UI shown. */
  domHelp?(anchor: string): void;
  /** Phase 5 — build/order STEP 2: replace the
   *  canvas UnitMenu (the gob CONTEXT MENU; PopupMenu's AddForm+DoModal) with a DOM
   *  overlay. When set, PopupMenu calls this INSTEAD of the canvas modal. The view is
   *  PERSISTENT + live-refreshing, so the seam is pull-shaped: `getModel()` returns the
   *  CURRENT read-model (built off the SAME InitMenu logic + the SetOwner screen-rect
   *  pipeline) or null when the menu should close (the gob deactivated/deselected); the
   *  view re-polls it on a ~25ms cadence (the canvas OnTimer cadence) so the buttons
   *  show/hide live AND the anchor follows the gob as the map scrolls. `onSelect(idc)`
   *  routes to the SAME OnMenuItemSelected the canvas continuation does (Build opens the
   *  build panel, Sell/Abort route to domConfirm, Repair/Research/Transform/Deliver
   *  enqueue here) — the view constructs NO command/Message. `onClose()` lets the view
   *  tell the host to tear down (AutoTakedown tap-elsewhere). Absent (a headless harness)
   *  ⇒ no UI shown. DISPLAY-only — no command path change. */
  domUnitMenu?(
    getModel: () => UnitMenuModel | null,
    onSelect: (idc: number) => void,
    onClose: () => void,
  ): void;
  /** Phase 5 — build/order STEP 3: replace the
   *  canvas UNIT BUILD carousel (MobileUnitBuildForm — kidfBuildVehicle "ORDER VEHICLE"
   *  / kidfBuildInfantry "RECRUIT PERSONNEL"; the VTS/HRC Build arm of OnMenuItemSelected
   *  opens this) with a DOM overlay. When set, the Build arm SetOwner's the form (so the
   *  EXISTING BuildFormDefUpdate / BuildFormOnUnitCompleted / BuilderDeactivate host
   *  seams keep firing on it — the live queue/progress/limit refresh) and hands the view
   *  a PULL-shaped model getter + action routers INSTEAD of AddForm+DoModal. `getModel()`
   *  returns the CURRENT read-model (built off the SAME SetOwner/UpdateUnitInfo/
   *  UpdateOrderButton logic) or null when the builder deactivated (→ close); the view
   *  re-polls it live. `onSelect(ut)` selects a unit (the SAME list Select → OnControlNotify
   *  → UpdateUnitInfo path); `onOrder()`/`onCancelOrder()` run the SAME OnControlSelected
   *  (kidcOrder/kidcCancelOrder) the canvas continuation does — the EXACT kmidBuildOther /
   *  kmidAbortBuildOther enqueue stays in buildui.ts (the view builds NO Message).
   *  `onClose()` ends the form (GetLastSelection persistence). Absent (a headless harness)
   *  ⇒ no UI shown. DISPLAY-only — no command path change. */
  domBuildPanel?(
    getModel: () => BuildPanelModel | null,
    onSelect: (ut: number) => void,
    onOrder: () => void,
    onCancelOrder: () => void,
    onClose: () => void,
    onHelp: () => void,
  ): void;

  /** Phase 5 — build/order STEP 3, FORM C: replace the canvas STRUCTURE BUILD
   *  carousel (StructureBuildForm — kidfBuildStructure "ORDER BUILDING"; the HQ kidcBuild
   *  arm of OnMenuItemSelected opens this) with a DOM overlay. Like domBuildPanel but for
   *  the structure variant: getModel() returns the CURRENT read-model (variant 'structure'
   *  — P DEMAND/P SUPPLY, no queue/progress overlays); onSelect(ut) selects a structure;
   *  onBuild() runs "Build" — which does NOT enqueue: it ends the form with the selected
   *  structure's gt and the BuildUI continuation hands gt to gpfrmPlace (the CANVAS
   *  placement overlay, FORM F not ported — C-R10). onClose() ends the form (Close /
   *  backdrop tap → kidcCancel, GetLastSelection persistence — C-R9). Absent (a headless
   *  harness) ⇒ no UI shown. DISPLAY-only — no command path change
   *  (this form builds NO Message — C-R11/U1; the command is emitted later by FORM F). */
  domStructurePanel?(
    getModel: () => BuildPanelModel | null,
    onSelect: (ut: number) => void,
    onBuild: () => void,
    onClose: () => void,
    onHelp: () => void,
  ): void;

  /** Phase 5 — FORM E: replace the canvas RESEARCH carousel (UpgradeForm —
   *  kidfUpgrade "R&D TECHNOLOGIES"; the ResearchGob kidcResearch arm of OnMenuItemSelected
   *  opens this) with a DOM overlay. The SIMPLEST carousel: ONLY a COST meter + a
   *  PREREQUISITES block + a DESCRIPTION (NO armor/damage/speed/range/power rows — E-R3);
   *  2 items (Advanced HRC / Advanced VTS), an empty state ("NO UPGRADES AVAILABLE"). Like
   *  domStructurePanel but for the research variant: getModel() returns the live read-model
   *  (variant 'research'); onSelect(upgm) selects an upgrade (the SAME list Select →
   *  UpdateUpgradeInfo path); onResearch() runs "Research" — which does NOT enqueue: it ends
   *  the form with the selected upgrade's upgm (EndForm(upgm)) and the BuildUI continuation
   *  enqueues kmidUpgradeCommand (wfUpgrade per choice — E-R5) just as the canvas DoModal
   *  callback does (the command stays in buildui.ts, U1). onClose() ends the form (Close /
   *  backdrop tap → kidcCancel). Absent (a headless harness) ⇒ no UI shown.
   *  DISPLAY-only — no command path change (this form builds NO Message — E-R5/U1). */
  domUpgradePanel?(
    getModel: () => BuildPanelModel | null,
    onSelect: (upgm: number) => void,
    onResearch: () => void,
    onClose: () => void,
    onHelp: () => void,
  ): void;
}

/** Phase 5 — STEP 2: a pure read-model of the gob CONTEXT MENU for a given
 *  gob at a given instant — the buttons the canvas UnitMenu would show (InitMenu
 *  parity) + the gob's on-screen rect (SetOwner geometry, in GAME px; the host
 *  scales by kGameScale for CSS). Built by BuildUI.GetMenuModel; re-polled live. */
export interface UnitMenuModel {
  /** the gob title (kidcTitle = punt.GetConsts().szName); rendered UPPERCASE (kifntTitle
   *  is caps-only) + WHITE by the view. */
  title: string;
  /** the title TEXT colour — WHITE (the canvas kidcTitle has no SetTextColor → kifntTitle
   *  default), as a CSS string. NOT the side colour (that's the confirm DialogForm's bar). */
  titleColor: string;
  /** the VISIBLE reloc-buttons (InitMenu Show/Enable applied), in form order. */
  buttons: UnitMenuButton[];
  /** kidfMobileHqMenu only: the "Can't Transform…" label shown when Transform is
   *  hidden (a LABEL, not a button — kidcCantTransform 1201). null otherwise / when
   *  Transform is offered. */
  cantTransform: string | null;
  /** the gob's on-screen rect in GAME px (the SetOwner rcUIBounds pipeline). The DOM
   *  menu anchors adjacent to this, kept on-screen. */
  anchor: {
    x: number;
    y: number;
    w: number;
    h: number;
    /** the TITLE anchor top in game px — the gob's hilight-sprite top when it has one
     *  (the menu's gob is always the selected one, so it does), else the UIBounds top.
     *  The canvas puts the title above the SPRITE, not the UIBounds (buildui.ts:571-578). */
    titleTop: number;
  };
}

export interface UnitMenuButton {
  /** the control id (kidcBuild/kidcRepair/… — Build's continuation routes by this). */
  idc: number;
  /** an accessible text label for the button (the bitmap art carries the visible
   *  label/icon; this is the fallback + the test/aria handle). */
  label: string;
  /** !kfCtlDisabled — a disabled button is shown greyed (the canvas draws m_ptbmDisabled). */
  enabled: boolean;
  /** the button bitmap art (the EXACT canvas PNGs, recolored kside1) — up/down +
   *  disabled (when present). Rendered by the DOM view as a faithful bitmap button. */
  art: UnitMenuButtonArt;
}

/** the per-button bitmap art, extracted from the control's own TBitmaps (the canvas
 *  build_btn/repair_btn/abort/sell PNGs) at the side-1 team color. */
export interface UnitMenuButtonArt {
  up: { cx: number; cy: number; rgba: Uint8Array };
  down: { cx: number; cy: number; rgba: Uint8Array };
  disabled: { cx: number; cy: number; rgba: Uint8Array } | null;
}

/** Phase 5 — build/order STEP 3: a pure read-model of a CAROUSEL BUILD panel, for
 *  the UNIT carousel (FORM D — kidfBuildVehicle "ORDER VEHICLE" / kidfBuildInfantry
 *  "RECRUIT PERSONNEL", MobileUnitBuildForm), the STRUCTURE carousel (FORM C —
 *  kidfBuildStructure "ORDER BUILDING", StructureBuildForm), and the RESEARCH carousel
 *  (FORM E — kidfUpgrade "R&D TECHNOLOGIES", UpgradeForm). Built by the form it is
 *  SetOwner'd on, at this instant — the SAME list/stat state the canvas form computes
 *  (SetOwner population + UpdateUnitInfo/UpdateStructureInfo/UpdateUpgradeInfo + UpdateOrderButton).
 *  Re-polled live (the canvas DefUpdate cadence). DISPLAY-only: no command/Message, no sim mutation.
 *
 *  The `variant` selects the chrome (FORM C / D / E, build-forms-spec):
 *   - 'unit' (FORM D): cyItem=17; SPEED/RANGE stat rows; LIVE queue-count + yellow
 *     build-progress per-item overlays; Order / Cancel Order / Close buttons; the Order
 *     loop enqueues (kmidBuildOther / kmidAbortBuildOther) via buildui.ts.
 *   - 'structure' (FORM C): cyItem=18; P DEMAND / P SUPPLY stat rows; NO per-item overlays
 *     (the canvas SetQueueInfo(null,null) — buildui.ts:1056); a single "Build" button +
 *     "Close" (NO Order/CancelOrder). "Build" RETURNS A GOB TYPE (EndForm(gt),
 *     buildui.ts:1178) and ENQUEUES NOTHING — the HQ continuation hands gt to gpfrmPlace
 *     (the canvas placement overlay, FORM F not ported).
 *   - 'research' (FORM E): cyItem=18; ONLY a COST meter row (NO armor/damage/speed/range/power
 *     — E-R3) + a PREREQUISITES label + multiline value + the DESCRIPTION; 2 items (Advanced
 *     HRC / Advanced VTS, icons hrc1/vts1); already-owned upgrades omitted; an empty-state
 *     ("NO UPGRADES AVAILABLE" → Research hidden — E-R3). A single "Research" button + "Close".
 *     "Research" RETURNS THE upgm (EndForm(upgm)) and ENQUEUES NOTHING — the gob-menu Research
 *     continuation enqueues kmidUpgradeCommand (E-R5). The carousel item `ut` field carries the
 *     upgm (the selection key), `queueCount`/`buildProgress` stay 0/null (no overlays). */
export interface BuildPanelModel {
  /** which carousel this is — selects the chrome (stat rows / overlays / buttons). */
  variant: 'unit' | 'structure' | 'research';
  /** the heading baked in forms.ini (LABEL=0 (2 5 82 0)): "ORDER VEHICLE" (VTS) /
   *  "RECRUIT PERSONNEL" (HRC) / "ORDER BUILDING" (structure) / "R&D TECHNOLOGIES" (research). */
  heading: string;
  /** the scrolling icon list, in form order (the buildable unit types / upgrades). For the
   *  research variant the item `ut` carries the upgm (kupgmAdvancedHRC/VTS — the select key). */
  items: BuildPanelItem[];
  /** the selected-unit stat block (null when nothing is selectable / selected). */
  info: BuildPanelInfo | null;
  /** m_fLimitReached — at the cap → show kidcLimitReached, hide Order/Build (C-R8/D-R5).
   *  research: always false (no limit on FORM E). */
  limitReached: boolean;
  /** unit variant: the Order button is shown (kidcOrder visible: m_fOrderValid && !limit).
   *  structure variant: the "Build" button is shown (kidcOk visible: a non-disabled item is
   *  selected && !limit — buildui.ts:1065-1071,1143-1146).
   *  research variant: the "Research" button is shown (kidcOk visible: a non-disabled item is
   *  selected && upgrades available — E-R3/E-R4). */
  orderShown: boolean;
  /** unit variant ONLY: the Cancel Order button is shown (kidcCancelOrder: queue count > 0).
   *  structure / research variant: always false (no Cancel Order on FORM C/E). */
  cancelOrderShown: boolean;
  /** research variant ONLY: the empty state — no upgrades available (cUpgrades===0). The view
   *  shows "NO UPGRADES AVAILABLE" (the kidcName text), hides Research + the cost/prereq labels
   *  (E-R3, buildui.ts:940-950). false for unit/structure. */
  emptyState?: boolean;
}

/** one icon-list row (BuildListControl item — the unit-type icon + its live overlays). */
export interface BuildPanelItem {
  /** the unit type (the ListItem pvData) — selecting/ordering route by this. */
  ut: number;
  /** the side-recoloured 'icon' sprite frame (anidFor(szAniName).GetStripIndex('icon')),
   *  as a game-px RGBA buffer — the DOM sizes it by kGameScale (T3). */
  icon: { cx: number; cy: number; rgba: Uint8Array } | null;
  /** the item is prereq/God-mode disabled — the canvas draws a Shadow over it. */
  disabled: boolean;
  /** the item is the current selection (the canvas draws a 2px white border). */
  selected: boolean;
  /** the per-item private-queue count (drawn bottom-right when > 0), the live overlay. */
  queueCount: number;
  /** the yellow build-progress fraction [0,1] for the unit CURRENTLY building
   *  (UnitBuildInProgress()===ut → m_fxHealthBuilding/armorStrength), else null. */
  buildProgress: number | null;
}

/** the selected-unit stat block (UpdateUnitInfo / UpdateStructureInfo: the labels + the
 *  pip/damage meters). The unit variant fills cost/armor/SPEED/RANGE; the structure variant
 *  fills cost/armor/P DEMAND/P SUPPLY (speed/range stay empty/0). */
export interface BuildPanelInfo {
  name: string;
  // *Meter = the exact pip COUNT the canvas PipMeterControl would blit (GetPipCount), so
  // the DOM renders the SAME number of pips — NOT a 0..100 value. meterMax = the count at
  // value=100; all the pip meters share the 71-wide rect (PIPMETER ... 71 9 in forms.ini),
  // so ONE max sizes every pip row to count/max — true for BOTH variants (the structure
  // power meters use a different SCALE but the SAME rect width, so the same full-scale count).
  meterMax: number;
  cost: string;
  costMeter: number; // kidcCost + kidcCostMeter
  armor: string;
  armorMeter: number; // kidcArmorStrength + kidcArmorStrengthMeter
  // unit variant rows (kidfBuildVehicle/Infantry):
  speed: string;
  speedMeter: number; // kidcMoveRate + kidcMoveRateMeter
  range: string;
  rangeMeter: number; // kidcWeaponRange + kidcWeaponRangeMeter
  // structure variant rows (kidfBuildStructure): P DEMAND / P SUPPLY (kidcPowerDemand/Supply
  // + their meters, both scaled by gnPowerSupplyMax — UpdateStructureInfo buildui.ts:1093-1107).
  powerDemand?: string;
  powerDemandMeter?: number;
  powerSupply?: string;
  powerSupplyMeter?: number;
  /** the per-target-class damage rows (kidcWeaponStrengthMeter, DamageMeter pip counts). */
  damage: { infantry: number; vehicle: number; structure: number };
  /** the description, or "This unit requires: …." / "This building requires: …." when
   *  the item is disabled. */
  description: string;
  // research variant rows (kidfUpgrade): the PREREQUISITES label + multiline value
  // (kidcPrerequisitesLabel / kidcPrerequisites — UpdateUpgradeInfo buildui.ts:973-975). The
  // cost LABEL + prereq LABEL are shown/hidden by SetOwner/UpdateUpgradeInfo (E-R3); the view
  // reflects costLabelShown/prerequisitesShown so the empty state hides both labels.
  prerequisites?: string;
  costLabelShown?: boolean;
  prerequisitesShown?: boolean;
}

// the menu forces the side-1 team palette on each button (SetOwner:421-422 sets
// kfCtlUseSide1Colors so the dark-blue art reads right) — render the bitmaps with it.
const kMenuButtonSide = 1; // kside1 (misccontrols.ts), the OnPaint side for these buttons

// per-idc accessible text label (the bitmaps carry the visible label/icon; this is the
// aria/test handle + the no-art fallback). The forms.ini menu buttons all use ""; these
// mirror the bitmap captions. NOTE the idc collisions (1304 = Build/AbortUpgrade, 1306 =
// Transform/Deliver) — but within ANY single menu form each idc appears once, and the
// distinct-art buttons (build vs abort-build, transform vs deliver) never co-occur, so a
// per-idc label is unambiguous in practice: 1304's form either offers Build (HQ/HRC/VTS)
// or AbortUpgrade (Research, where 1305 is Research) — both are labelled by their bitmap.
const kMenuButtonLabel = new Map<number, string>([
  [kidcRepair, 'Repair'],
  [kidcSelfDestruct, 'Sell'],
  [kidcAbortBuild, 'Abort Build'],
  [kidcAbortRepair, 'Abort Repair'],
  [kidcBuild, 'Build'], // == kidcAbortUpgrade (1304); the Research form has no kidcBuild
  [kidcResearch, 'Research'],
  [kidcTransform, 'Transform'], // == kidcDeliver (1306)
]);

// the text-only buttons (Transform/Deliver) carry no bitmap art.
const kTextButtonArt: UnitMenuButtonArt = {
  up: { cx: 0, cy: 0, rgba: new Uint8Array(0) },
  down: { cx: 0, cy: 0, rgba: new Uint8Array(0) },
  disabled: null,
};

// a Color → CSS rgb() string.
function cssColor(clr: { r: number; g: number; b: number }): string {
  return `rgb(${clr.r}, ${clr.g}, ${clr.b})`;
}

// render a TBitmap into a plain RGBA buffer at the side-1 team color (the SAME path
// the canvas ButtonControl.OnPaint blits, misccontrols.ts:344-345). null when absent.
function tbmToRgba(ptbm: TBitmap | null): { cx: number; cy: number; rgba: Uint8Array } | null {
  if (ptbm === null) return null;
  const { cx, cy } = ptbm.GetSize();
  if (cx <= 0 || cy <= 0) return null;
  const dib = new CpuDib(cx, cy);
  ptbm.BltTo(dib, 0, 0, kMenuButtonSide, null);
  return { cx, cy, rgba: dib.rgba };
}

// canvas-removal R3 step (i): the in-game build/recruit/research carousels are no longer
// `extends Form` control-hosts (no misccontrols ListControl/LabelControl/PipMeterControl tree).
// Each carousel holds its buildable list as plain BuildItemRec records and computes its model
// DIRECTLY from the sim — the SAME values the canvas SetOwner/Update*Info wrote into the control
// tree, byte-identical (build-forms-spec; modal-build.test.ts pins the kmid* commands + model).
/** one row of a build carousel — the SAME (panid, nStrip, nFrame, pvData, fDisabled, fSelected)
 *  a BuildListControl ListItem carried (Builder.cpp:942-955), minus the Form/control machinery. */
interface BuildItemRec {
  /** the unit type (or the upgm for the research carousel) — the list pvData / select key. */
  pv: number;
  /** the 'icon' strip the canvas DrawItem blits (anidFor(szAniName).GetStripIndex('icon')). */
  panid: import('../gfx/animdata.ts').ClientAnimationData;
  nStrip: number;
  /** the icon frame (always 0 here, as AddAnim was called). */
  nFrame: number;
  /** prereq / God-mode disabled (the canvas draws a Shadow over it). */
  fDisabled: boolean;
  /** the current selection (the canvas draws a 2px white border). */
  fSelected: boolean;
}

// Phase 5 — build/order STEP 3: render a build-list item's 'icon' sprite frame
// into a plain RGBA buffer at the local player's side colour — the SAME panid/nStrip/
// nFrame the BuildListControl.DrawItem blits (Builder.cpp:1031-1040), at the frame's
// NATURAL size (DrawFrame at -bounds.left/-bounds.top). null when the item has no icon strip.
function ExtractListIcon(
  pli: BuildItemRec,
  side: number,
): { cx: number; cy: number; rgba: Uint8Array } | null {
  const panid = pli.panid;
  const nStrip = pli.nStrip;
  const nFrame = pli.nFrame;
  const b = panid.GetBounds(nStrip, nFrame);
  const cx = b.right - b.left;
  const cy = b.bottom - b.top;
  if (cx <= 0 || cy <= 0) return null;
  const dib = new CpuDib(cx, cy);
  panid.DrawFrame(nStrip, nFrame, dib, -b.left, -b.top, side);
  return { cx, cy, rgba: dib.rgba };
}

// The build-form PIPMETER pip COUNT, computed WITHOUT a PipMeterControl — the EXACT formula
// PipMeterControl.GetPipCount used (misccontrols.cpp:1808-1826 OnPaint / the GetPipCount mirror):
// cxPips = trunc(rcWidth * value / 100); cPips = trunc((cxPips + trunc(pipW/2)) / pipW).
// rcWidth is the scaled meter rect width (all build PIPMETERs share the 71-fc rect → ONE width),
// pipW is the static pip.png width. value is the 0..100 the canvas SetValue got.
function PipMeterCount(rcWidth: number, value: number, pipW: number): number {
  if (pipW <= 0) return 0;
  const cxPips = Math.trunc((rcWidth * value) / 100);
  return Math.trunc((cxPips + Math.trunc(pipW / 2)) / pipW);
}

// the per-target-class DAMAGEMETER pip COUNT — the SAME math as DrawPips
// (misccontrols.ts:1941-1957): cPips = round(8 * fxDamagePerSec / kfxDamagePerSecMax),
// clamped to 8; 0 when nDamage is 0. The DOM draws this many pip glyphs per row.
const kfxDamagePerSecMaxLocal = itofx(16); // misccontrols.cpp:1871
function DamagePipCount(nDamage: number, ctFiringRate: number): number {
  if (nDamage === 0 || ctFiringRate === 0) return 0;
  const fxDamagePerSec = mulfx(itofx(nDamage), divfx(itofx(100), itofx(ctFiringRate)));
  let cPips = fxtoi(
    addfx(divfx(8 * fxDamagePerSec, kfxDamagePerSecMaxLocal), Math.trunc(itofx(1) / itofx(2))),
  );
  if (cPips > 8) cPips = 8;
  return cPips;
}

// game/Unit.cpp:40-60
export function GetPrerequisiteString(puntc: UnitConsts, ctx: SimContext): string {
  let psz = '';
  for (let i = 0; i < kutMax; i++) {
    if (puntc.umPrerequisites & (1 << i)) {
      if (psz !== '') psz += ', ';
      psz += ctx.unitConsts[i].szLongName;
    }
  }

  for (let i = 0; i < gaupgNames.length; i++) {
    if (puntc.upgmPrerequisites & (1 << i)) {
      if (psz !== '') psz += ', ';
      psz += gaupgNames[i];
      psz += ' upgrade';
    }
  }
  return psz;
}

// game/GameObjects.cpp:2228
const kctMaxDistFromNeighbor = 3;

// game/GameObjects.cpp:2222-2298 — a GobMgr method in the C++; it reads
// gpmfrmm (the minimap dead zone), so it lives client-side
export function GobMgrIsStructurePlacementValid(
  ui: BuildUI,
  pstruc: StructConsts,
  tx: number,
  ty: number,
  pplr: Player,
): boolean {
  const ctx = ui.deps.ctx;
  const { ctx: m_ctx, cty: m_cty } = ctx.gobm.GetMapSize();

  // No part of the structure can be off an edge of the map

  if (tx < 0 || ty < 0 || tx + pstruc.ctxReserve > m_ctx || ty + pstruc.ctyReserve > m_cty) {
    return false;
  }

  // No part of the structure can be under the inaccessible part of the
  // minimap (when the map is scrolled to the bottom right corner)

  const c = ui.deps.gctx.coords;
  const rcMiniMap = ui.deps.getMiniMapRect();
  if (rcMiniMap !== null) {
    const mmWidth = rcMiniMap.right - rcMiniMap.left;
    const mmHeight = rcMiniMap.bottom - rcMiniMap.top;
    const wxMiniMapLeft = WcFromTc(m_ctx) - c.WcFromUpc(mmWidth);
    const wyMiniMapTop = WcFromTc(m_cty) - c.WcFromUpc(mmHeight);
    const wxPlacementLeft = WcFromTc(tx);
    const wyPlacementTop = WcFromTc(ty);
    if (wxPlacementLeft > wxMiniMapLeft - kwcTile && wyPlacementTop > wyMiniMapTop - kwcTile) {
      return false;
    }
  }

  const trcSearch = {
    left: tx - kctMaxDistFromNeighbor,
    top: ty - kctMaxDistFromNeighbor,
    // C++ pstruc->ctx/cty — the footprint tile size (renamed ctxTiles/ctyTiles
    // in the TS StructConsts: the SimContext field shadows `ctx`)
    right: tx + pstruc.ctxTiles + kctMaxDistFromNeighbor,
    bottom: ty + pstruc.ctyTiles + kctMaxDistFromNeighbor,
  };

  // We let the player put towers a little farther away from neighboring
  // structures -- but not chain them! (see below)

  if (pstruc.um & kumTowers) {
    trcSearch.left -= 1;
    trcSearch.top -= 1;
    trcSearch.right += 1;
    trcSearch.bottom += 1;
  }

  // Don't search off any edge of the map

  if (trcSearch.left < 0) trcSearch.left = 0;
  if (trcSearch.right > m_ctx) trcSearch.right = m_ctx;
  if (trcSearch.top < 0) trcSearch.top = 0;
  if (trcSearch.bottom > m_cty) trcSearch.bottom = m_cty;

  for (let tyT = trcSearch.top; tyT < trcSearch.bottom; tyT++) {
    for (let txT = trcSearch.left; txT < trcSearch.right; txT++) {
      const pgob = ctx.gobm.GetShadowGob(txT, tyT);
      if (pgob === null) continue;
      if ((pgob.GetFlags() & (kfGobStructure | kfGobActive)) === (kfGobStructure | kfGobActive)) {
        if (pgob.GetOwner() !== pplr) continue;

        // Special hack to keep players from building long chains of towers

        const gt = pgob.GetType();
        if (gt !== kgtMachineGunTower && gt !== kgtRocketTower) {
          return true;
        }
      }
    }
  }

  return false;
}

//
// UnitMenu (game/Unit.cpp:1079-1350)
//

// game/Unit.cpp:1103-1105
const kcxButtonSpace = 0;
const kcyTitleMargin = 3;
const kcxTitleMargin = 5;

// canvas-removal R3 step (i): the per-gob context menu / hover title is no longer a Form. The
// menu's buttons + title live as plain records. A record carries the EXACT canvas state the
// context-menu read-model + the hover-title canvas render need: the bitmaps (build_btn/repair/…),
// the rect (scaled from the section geometry), the font, and the kfCtl* flag word (so InitMenu's
// Show/Enable mutate it just as it mutated the ButtonControl, and GetMenuModel reads it back).

/** one menu button record — replaces the ButtonControl in the menu control tree. */
class MenuButtonRec {
  readonly idc: number;
  readonly nfnt: number;
  m_rc = new Rect(); // scaled, form-relative (the canvas control m_rc)
  m_wf = kfCtlVisible; // the kfCtl* flag word (Init defaults to visible)
  m_ptbmUp: TBitmap | null = null;
  m_ptbmDown: TBitmap | null = null;
  m_ptbmDisabled: TBitmap | null = null;
  m_szLabel: string;
  constructor(idc: number, nfnt: number, szLabel: string) {
    this.idc = idc;
    this.nfnt = nfnt;
    this.m_szLabel = szLabel;
  }
  GetId(): number {
    return this.idc;
  }
  GetFlags(): number {
    return this.m_wf;
  }
  SetFlags(wf: number): void {
    this.m_wf = wf;
  }
  // Control.Show / Control.Enable (form.cpp:1047-1075) — mutate only the kfCtlVisible / kfCtlDisabled bit.
  Show(fShow: boolean): void {
    if (fShow) this.m_wf |= kfCtlVisible;
    else this.m_wf &= ~kfCtlVisible;
  }
  Enable(fEnable: boolean): void {
    if (fEnable) this.m_wf &= ~kfCtlDisabled;
    else this.m_wf |= kfCtlDisabled;
  }
}

/** the parsed menu definition (parsed ONCE per gob-type menu form from kBuildFormSections —
 *  the SAME geometry/bitmaps Form.Init+ButtonControl.Init produced, byte-identical). */
interface MenuDef {
  /** the title LABEL=1200 nfnt (3 = kifntTitle) + its form-relative scaled rect. */
  titleNfnt: number;
  /** the reloc buttons (kidcRelocButtonMin..Max), in form order. */
  buttons: MenuButtonRec[];
  /** the "Can't Transform…" label (kidcCantTransform 1201) text + visibility flag (MobileHQ). */
  cantTransform: { m_wf: number; text: string } | null;
}

// parse a menu form section into a MenuDef (replaces Form.Init's control-tree build for menus).
// The button rects are scaled by PcFromFc(host.gcxFullScreenFormDiv10) exactly as Control.Init
// did; ButtonControl.InitParts resized the rect to max(up,down) bitmap size — reproduced here.
function parseMenuDef(
  section: import('../../../sim/src/data/ini.ts').IniSection,
  host: import('../render/paintengine.ts').FormMgr['host'],
): MenuDef {
  const div10 = host.gcxFullScreenFormDiv10;
  const buttons: MenuButtonRec[] = [];
  let titleNfnt = kifntTitle;
  let cantTransform: { m_wf: number; text: string } | null = null;
  for (const p of section.props) {
    if (p.key === 'LABEL') {
      const r = IniScanf(p.value, '%d (%*d %*d %*d %*d) "%s" %d');
      const idc = (r.values[0] as number) ?? 0;
      const nfnt = (r.values[2] as number) ?? 0;
      if (idc === kidcTitle) titleNfnt = nfnt;
      else if (idc === kidcCantTransform) {
        cantTransform = { m_wf: kfCtlVisible, text: (r.values[1] as string) ?? '' };
      }
    } else if (p.key === 'BUTTON') {
      // idc (x y cx cy) "label" nfnt center? up.tbm down.tbm disabled?.tbm
      const r = IniScanf(p.value, '%d (%d %d %d %d) "%s" %d %s %s %s %s');
      const idc = r.values[0] as number;
      if (idc < kidcRelocButtonMin || idc >= kidcRelocButtonMax) continue;
      let x = r.values[1] as number,
        y = r.values[2] as number;
      let cx = r.values[3] as number,
        cy = r.values[4] as number;
      const szLabel = (r.values[5] as string) ?? '';
      const nfnt = (r.values[6] as number) ?? 0;
      // the trailing args mirror ButtonControl.Init's cArgs switch (center flag + 0/2/3 bitmaps).
      const tail: string[] = [];
      for (let i = 7; i < r.values.length; i++) tail.push(r.values[i] as string);
      // scale form-relative rect (Control.Init kfFrmScaleCoords arm — menus scale).
      x = PcFromFc(x, div10);
      y = PcFromFc(y, div10);
      cx = PcFromFc(cx, div10);
      cy = PcFromFc(cy, div10);
      const rec = new MenuButtonRec(idc, nfnt, szLabel);
      rec.m_rc.Set(x, y, x + cx, y + cy);
      // ButtonControl.InitParts: the bitmap names are tail entries AFTER an optional 'center'.
      // The menu buttons are all 'nocenter' with up/down(/disabled) — cArgs 5 or 6 (the names
      // start at tail[1] since tail[0] is the 'nocenter'/'center' flag).
      const fCenter = tail[0] === 'center';
      const szUp = tail[1] ?? '',
        szDown = tail[2] ?? '',
        szDisabled = tail[3] ?? '';
      if (szUp !== '') rec.m_ptbmUp = host.createTBitmap(szUp);
      if (szDown !== '') rec.m_ptbmDown = host.createTBitmap(szDown);
      if (szDisabled !== '') rec.m_ptbmDisabled = host.createTBitmap(szDisabled);
      // ButtonControl.InitParts: when up+down bitmaps exist, the rect is resized to max(up,down).
      if (rec.m_ptbmUp !== null && rec.m_ptbmDown !== null) {
        const s1 = rec.m_ptbmUp.GetSize();
        const s2 = rec.m_ptbmDown.GetSize();
        const bcx = Math.max(s1.cx, s2.cx),
          bcy = Math.max(s1.cy, s2.cy);
        if (bcx !== 0 && bcy !== 0) {
          rec.m_rc.right = rec.m_rc.left + bcx;
          rec.m_rc.bottom = rec.m_rc.top + bcy;
        }
      }
      if (fCenter) rec.m_rc.Offset(-Math.trunc(rec.m_rc.Width() / 2), 0);
      buttons.push(rec);
    }
  }
  return { titleNfnt, buttons, cantTransform };
}

export class UnitMenu {
  m_punt: UnitGob | null = null;
  m_rcButtons = new Rect();
  m_rc = new Rect(); // the form rect (the hover-title render offsets controls by it)
  private readonly ui: BuildUI;
  private readonly def: MenuDef;
  // the title strip layout (computed by SetOwner) — its form-relative rect + the rendered text.
  private m_rcTitle = new Rect();
  private m_szTitle = '';

  constructor(ui: BuildUI, def: MenuDef) {
    this.ui = ui;
    this.def = def;
  }

  // game/Unit.cpp:1082-1085
  GetOwner(): UnitGob | null {
    return this.m_punt;
  }

  // the InitMenu / GetMenuModel control-host surface: find a button record by idc (the menu
  // dispatch InitMenu/StructInitMenu calls GetControlPtr(idc).Show/Enable on these).
  GetButton(idc: number): MenuButtonRec | null {
    for (const b of this.def.buttons) if (b.idc === idc) return b;
    return null;
  }
  get buttons(): readonly MenuButtonRec[] {
    return this.def.buttons;
  }
  get cantTransformRec(): { m_wf: number; text: string } | null {
    return this.def.cantTransform;
  }

  // game/Unit.cpp:1107-1283 — layout (used by the hover title render). fPerUnitInit=true runs
  // the gob's InitMenu (the context menu does this in GetMenuModel; the hover title passes false).
  SetOwner(punt: UnitGob, fPerUnitInit = true): void {
    this.m_punt = punt;

    // Let the owning Gob initialize the menu state (hide/show/disable buttons).
    if (fPerUnitInit) {
      this.ui.InitMenu(this.m_punt, this);
    }

    const pszTitle = this.m_punt.GetConsts().szName;
    const host = this.ui.deps.mfrmm.host;
    const c = this.ui.deps.gctx.coords;

    // Calc the width/height of the 'button bar' (all visible buttons). Each button is forced to
    // the side1 colors (some use side1's dark blues) — set in the record's flag word.
    let cButtons = 0;
    let cxButton = 0,
      cyButton = 0;
    for (const b of this.def.buttons) {
      b.m_wf |= 16; /* kfCtlUseSide1Colors */
      if (!(b.m_wf & kfCtlVisible)) continue;
      if (cButtons === 0) {
        cxButton = b.m_rc.Width();
        cyButton = b.m_rc.Height();
      }
      cButtons++;
    }
    const cxButtons = (cxButton + kcxButtonSpace) * cButtons - kcxButtonSpace;

    // Calc the width/height of the title bar
    const cxTitleText = host.getFont(kifntTitle).GetTextExtent(pszTitle);
    const cxTitleWhole = cxTitleText + kcxTitleMargin * 2;
    const cyFont = host.getFont(kifntTitle).GetHeight();
    const cyTitle = cyFont + kcyTitleMargin * 2;

    // Gob's UIBounds in screen coordinates (the SAME pipeline the selection rect draws with).
    const wpt = { wx: 0, wy: 0 };
    this.ui.deps.sui.GetViewPos(wpt);
    const xViewOrigin = c.PcFromUwc(wpt.wx) & 0xfffe;
    const yViewOrigin = c.PcFromUwc(wpt.wy) & 0xfffe;

    const b = this.m_punt.GetConsts().wrcUIBounds;
    const rcUIBounds = new Rect();
    rcUIBounds.Set(
      c.PcFromWc(b.left),
      c.PcFromWc(b.top),
      c.PcFromWc(b.right),
      c.PcFromWc(b.bottom),
    ); // Rect::FromWorldRect
    const wptPos = { wx: 0, wy: 0 };
    this.m_punt.GetPosition(wptPos);
    const xPos = c.PcFromWc(wptPos.wx) - xViewOrigin;
    const yPos = c.PcFromWc(wptPos.wy) - yViewOrigin;
    rcUIBounds.Offset(xPos, yPos);

    // Set form size/position. Put title on top of hilight sprite, if there is one.
    const rcForm = new Rect();
    const rcSprite = this.ui.deps.GetAnimSpriteBounds(this.m_punt);
    if (rcSprite !== null) {
      const rcSpriteBounds = new Rect();
      rcSpriteBounds.SetRc(rcSprite);
      rcSpriteBounds.Offset(xPos, yPos);
      rcForm.top = rcSpriteBounds.top - cyTitle - kcyHealthBar;
    } else {
      rcForm.top = rcUIBounds.top - cyTitle - kcyHealthBar;
    }
    rcForm.bottom = rcUIBounds.bottom + cyButton;
    let cxForm = Math.max(rcUIBounds.Width(), cxTitleWhole);
    if (cxForm < cxButtons) cxForm = cxButtons;
    rcForm.left = rcUIBounds.left + Math.trunc(rcUIBounds.Width() / 2) - Math.trunc(cxForm / 2);
    rcForm.right = rcForm.left + cxForm;
    const cyForm = rcForm.Height();

    // Keep the form on screen
    const sizPlayfield = this.ui.deps.GetPlayfieldSize();
    let dx = 0,
      dy = 0;
    if (rcForm.left < 0) dx = -rcForm.left;
    else if (rcForm.right > sizPlayfield.cx) dx = sizPlayfield.cx - rcForm.right;
    if (rcForm.top < 0) dy = -rcForm.top;
    else if (rcForm.bottom > sizPlayfield.cy) dy = sizPlayfield.cy - rcForm.bottom;
    rcForm.Offset(dx, dy);
    this.m_rc.SetRc(rcForm);

    // Set title size/position (dx/dy keep the buttons near the partially off-screen unit).
    const rcTitle = new Rect();
    rcTitle.left = Math.trunc((cxForm - cxTitleText) / 2) - dx;
    if (rcTitle.left < 0) rcTitle.left = 0;
    else if (rcTitle.left + cxTitleText > cxForm)
      rcTitle.left += cxForm - (rcTitle.left + cxTitleText);
    rcTitle.top = kcyTitleMargin - dy;
    if (rcTitle.top < kcyTitleMargin) rcTitle.top = kcyTitleMargin;
    else if (rcTitle.top - kcyTitleMargin + cyTitle > cyForm - cyButton)
      rcTitle.top += cyForm - cyButton - (rcTitle.top - kcyTitleMargin + cyTitle);
    rcTitle.right = rcTitle.left + cxTitleText;
    rcTitle.bottom = rcTitle.top + cyFont;
    this.m_rcTitle.SetRc(rcTitle);
    this.m_szTitle = pszTitle;

    // Set the button positions. dx/dy are used as above for the title.
    const rcButton = new Rect();
    rcButton.left = Math.trunc((cxForm - cxButtons) / 2) - dx;
    if (rcButton.left < 0) rcButton.left = 0;
    else if (rcButton.left + cxButtons > cxForm)
      rcButton.left += cxForm - (rcButton.left + cxButtons);
    rcButton.top = cyTitle + kcyHealthBar + rcUIBounds.Height() - dy;
    if (rcButton.top < cyTitle) rcButton.top = cyTitle;
    else if (rcButton.top + cyButton > cyForm) rcButton.top = cyForm - cyButton;
    rcButton.bottom = rcButton.top + cyButton;

    // Stash away the button-bar border rect for painting later.
    this.m_rcButtons.SetRc(rcButton);
    this.m_rcButtons.right = this.m_rcButtons.left + cxButtons;
    this.m_rcButtons.Offset(this.m_rc.left, this.m_rc.top);

    // Position the buttons.
    for (const btn of this.def.buttons) {
      if (!(btn.m_wf & kfCtlVisible)) continue;
      rcButton.right = rcButton.left + cxButton;
      btn.m_rc.SetRc(rcButton);
      rcButton.left += cxButton + kcxButtonSpace;
    }

    // HACK: a single button is a Transform/Deliver — no border around it.
    if (cButtons === 1) this.m_rcButtons.SetEmpty();
  }

  // game/Unit.cpp:1285-1308 (OnPaintBackground) — the title-strip shadow + the black borders
  // around the title and the button bar. pupd may be null (full paint).
  PaintTitleBackground(pbm: CpuDibBitmap, pupd: UpdateMap | null): void {
    // Draw title background (shadow under the title label rect).
    const rc = new Rect();
    rc.SetRc(this.m_rcTitle);
    rc.Offset(this.m_rc.left, this.m_rc.top);
    rc.Inflate(kcxTitleMargin - 1, kcyTitleMargin - 1); // leave 1px for the black border
    ShadowHelper(pbm, pupd, rc);

    // Draw border around title.
    rc.Inflate(1, 1);
    DrawBorder(pbm, rc, 1, GetColor(iclr.kiclrBlack), pupd);

    // Draw border around buttons.
    if (!this.m_rcButtons.IsEmpty())
      DrawBorder(pbm, this.m_rcButtons, 1, GetColor(iclr.kiclrBlack), pupd);
  }

  // game/Unit.cpp:1310-1313 (OnPaintControls) — the title label (DrawFancyText) + each visible
  // button bitmap (side1, up state — the static title is never pressed/disabled).
  PaintTitleControls(pbm: CpuDibBitmap): void {
    const host = this.ui.deps.mfrmm.host;
    const pfnt = host.getFont(this.def.titleNfnt);
    DrawFancyText(
      pbm,
      pfnt,
      this.m_szTitle,
      this.m_rcTitle.left + this.m_rc.left,
      this.m_rcTitle.top + this.m_rc.top,
    );
    for (const btn of this.def.buttons) {
      if ((btn.m_wf & kfCtlVisible) === 0) continue;
      // ButtonControl.OnPaint: disabled→disabled bitmap, else up (not selected in a static title).
      const ptbm = btn.m_wf & kfCtlDisabled ? btn.m_ptbmDisabled : btn.m_ptbmUp;
      if (ptbm === null) continue;
      ptbm.BltTo(
        pbm,
        btn.m_rc.left + this.m_rc.left,
        btn.m_rc.top + this.m_rc.top,
        btn.m_wf & 16 /* kfCtlUseSide1Colors */ ? kMenuButtonSide : ksideNeutral,
      );
      // a labeled button (Transform/Deliver) centers its text — none of the bitmap menu buttons
      // carry a label, but reproduce the canvas path for completeness.
      if (btn.m_szLabel !== null && btn.m_szLabel.length !== 0) {
        const bpfnt = host.getFont(btn.nfnt);
        const cx = bpfnt.GetTextExtent(btn.m_szLabel);
        const cy = bpfnt.GetHeight();
        const x = btn.m_rc.left + Math.trunc((btn.m_rc.Width() - cx + 1) / 2);
        const y = btn.m_rc.top + Math.trunc((btn.m_rc.Height() - cy + host.gcxyBorder) / 2);
        bpfnt.DrawText(pbm, btn.m_szLabel, x + this.m_rc.left, y + this.m_rc.top);
      }
    }
  }

  // FingerHandler::ShowUnitTitle teardown (the C++ delete m_pfrmUnitTitle) — nothing to free.
  Dispose(): void {
    this.m_punt = null;
  }
}

// canvas-removal R3 step (i): the FingerHandler hover-title flourish (FingerHandler.ShowUnitTitle,
// fingerhandler.cpp:668-690) is a PaintLayer wrapping a UnitMenu — AddLayer'd to the sim region
// FormMgr (mfrmm) so the FormMgr paint walk renders the title strip via OnPaintBackground (shadow
// + borders) + OnPaintControls (label + buttons), EXACTLY as the canvas Form did, with no Form.
export class UnitTitleLayer implements PaintLayer {
  m_pfrmm: FormMgr | null = null;
  m_rc = new Rect();
  m_idf = kidfUnitMenu;
  m_wf = kfFrmVisible | kfFrmTranslucent; // the title is translucent (shadow), like the canvas menu
  private readonly menu: UnitMenu;

  constructor(menu: UnitMenu) {
    this.menu = menu;
    this.m_rc.SetRc(menu.m_rc); // the menu's computed form rect (the layer's geometry)
  }

  GetId(): number {
    return this.m_idf;
  }
  GetFlags(): number {
    return this.m_wf;
  }
  GetFormMgr(): FormMgr | null {
    return this.m_pfrmm;
  }
  GetMenu(): UnitMenu {
    return this.menu;
  }

  OnPaintBackground(pbm: CpuDibBitmap, pupd: UpdateMap | null): void {
    this.menu.PaintTitleBackground(pbm, pupd);
  }
  OnPaint(_pbm: CpuDibBitmap): void {}
  OnPaintControls(pbm: CpuDibBitmap, _pupd: UpdateMap | null): void {
    this.menu.PaintTitleControls(pbm);
  }
  // the title is not interactive (ShowUnitTitle is a non-modal flourish; the pen-up shows the
  // real menu). It claims no input — the SimUIForm layer underneath handles the pen.
  OnHitTest(_pevt: Event): boolean {
    return false;
  }
  OnKeyTest(_pevt: Event | null): boolean {
    return false;
  }
  OnPenEvent(_pevt: Event): boolean {
    return false;
  }
  EventProc(_pevt: Event): boolean {
    return false;
  }
  BreakCapture(): void {}
  OnUpdateMapInvalidate(pupd: UpdateMap, prcOpaque: Rect): void {
    // mark the title rect invalid so it paints (mirrors Form.OnUpdateMapInvalidate's control walk).
    if (!prcOpaque.RectIn(this.m_rc)) pupd.IsRectInvalidAndTrackDamage(this.m_rc);
  }
  ScrollInvalidate(_pupd: UpdateMap | null): void {}
  OnScroll(_dx: number, _dy: number): void {}
  FrameStart(): void {}
  FrameComplete(): void {}
  OnDisplayResize(): void {}
  InvalidateRect(prc: Rect | null): void {
    if (this.m_pfrmm !== null) this.m_pfrmm.InvalidateRect(prc ?? this.m_rc);
  }
  Dispose(): void {
    if (this.m_pfrmm !== null) this.m_pfrmm.RemoveForm(this);
    this.menu.Dispose();
  }
}

//
// UpgradeForm (game/Research.cpp:8-25 + 433-590) — the research-center modal
//

// canvas-removal R3 step (i): UpgradeForm is no longer `extends Form`. It holds the available
// upgrade list as plain BuildItemRec records (each carrying its Upgrade in `upg`) and computes
// BuildPanelModel DIRECTLY from the sim — the SAME values the canvas SetOwner/UpdateUpgradeInfo
// wrote into the control tree (Research.cpp:433-552), byte-identical. The DOM research carousel
// is the only consumer (?dom is the only runtime path); the headless harness arm is gone.
interface UpgradeItemRec extends BuildItemRec {
  upg: Upgrade;
}

export class UpgradeForm {
  m_pgobOwner: ResearchGob | null = null;
  private readonly ui: BuildUI;
  // the populated list + the selected upgm (-1 = none) — the canvas BuildListControl state.
  private items: UpgradeItemRec[] = [];
  private selUpgm = -1;
  // the empty-state (cUpgrades===0) flag — SetOwner shows "NO UPGRADES AVAILABLE" + hides Research.
  private fEmpty = false;

  constructor(ui: BuildUI) {
    this.ui = ui;
  }

  // Research.cpp:11-13
  GetOwner(): ResearchGob | null {
    return this.m_pgobOwner;
  }

  // game/Research.cpp:433-515 — populate the available-upgrade list from the sim (no control tree).
  SetOwner(pgobOwner: ResearchGob): void {
    const ctx = this.ui.deps.ctx;
    this.m_pgobOwner = pgobOwner;
    this.items = [];

    // Initialize list with available upgrade types
    const pplr = pgobOwner.GetOwner() as Player;
    const upgmOwned = pplr.GetUpgradeMask();
    const upgmAllowed = pplr.GetAllowedUpgrades();

    let cUpgrades = 0;
    const aupg = ctx.fMultiplayer ? gaupgMP : gaupg; // gfMultiplayer
    const panidIcons = this.ui.deps.gctx.anidFor('upgrades.anir'); // s_panidUpgradeIcons
    for (const pupg of aupg) {
      // already acquired? don't list it
      if (pplr.GetUpgradeMask() & pupg.upgm) continue;

      // prerequisites owned?
      let fDisabled = true;
      if (
        ctx.fGodMode ||
        ((upgmOwned & pupg.upgmPrerequisites) >>> 0 === pupg.upgmPrerequisites &&
          (pplr.GetUnitMask() & pupg.umPrerequisites) >>> 0 === pupg.umPrerequisites)
      ) {
        fDisabled = false;
      }

      // allowed for this player?
      if (ctx.fGodMode || pupg.upgm & upgmAllowed) {
        cUpgrades++;
        const nStripIcon = panidIcons !== null ? panidIcons.GetStripIndex(pupg.szIconName) : -1;
        if (nStripIcon !== -1) {
          this.items.push({
            pv: pupg.upgm,
            upg: pupg,
            panid: panidIcons!,
            nStrip: nStripIcon,
            nFrame: 0,
            fDisabled,
            fSelected: false,
          });
        }
      }
    }

    this.fEmpty = cUpgrades === 0;

    // Select the first upgrade by default (BuildListControl.Select(0)).
    if (this.items.length > 0) {
      this.items[0].fSelected = true;
      this.selUpgm = this.items[0].pv;
    } else {
      this.selUpgm = -1;
    }
  }

  // game/Research.cpp:554-558
  EndForm(): void {
    this.m_pgobOwner = null;
  }

  // ── FORM E seam — the SAME pull-model + action routers FORM C/D expose ────────

  GetDomOwner(): ResearchGob | null {
    return this.m_pgobOwner;
  }

  // the currently-selected list record (the canvas GetSelectedItem), or null.
  private selectedItem(): UpgradeItemRec | null {
    for (const it of this.items) if (it.fSelected) return it;
    return null;
  }

  /** a pure read-model of the research carousel at this instant — the SAME list/info state the
   *  canvas form computed (SetOwner population + UpdateUpgradeInfo, Research.cpp:516-552), built
   *  DIRECTLY from the sim. ONLY a COST meter + PREREQUISITES (no armor/damage/speed/range/power
   *  — E-R3); the empty state ("NO UPGRADES AVAILABLE") is surfaced as emptyState. DISPLAY-only. */
  BuildModel(heading: string): BuildPanelModel {
    const side = this.ui.deps.ctx.plrLocal!.GetSide();
    const mw = this.ui.meterWidthPx; // the scaled PIPMETER rect width (all build meters share it)
    const pw = this.ui.pipWidthPx;

    // the carousel — the upgrade items (icons hrc1/vts1, side-recoloured), `ut` = the upgm
    // (the select key). No queue count / build progress overlay (research has none).
    const items: BuildPanelItem[] = [];
    for (const it of this.items) {
      items.push({
        ut: it.pv,
        icon: ExtractListIcon(it, side),
        disabled: it.fDisabled,
        selected: it.fSelected,
        queueCount: 0,
        buildProgress: null,
      });
    }

    // the empty state — SetOwner shows "NO UPGRADES AVAILABLE" + hides Research + the cost/prereq
    // labels (E-R3). UpdateUpgradeInfo (the selected arm) sets the cost/name/desc/prereq text.
    const sel = this.selectedItem();
    let name = '',
      cost = '',
      costVal = 0,
      description = '',
      prerequisites = '';
    let costLabelShown = false,
      prerequisitesShown = false,
      researchShown = false;
    if (this.fEmpty || sel === null) {
      // SetOwner's cUpgrades===0 arm (Research.cpp:498-507): the labels the canvas set.
      name = 'NO UPGRADES AVAILABLE';
    } else {
      // UpdateUpgradeInfo (Research.cpp:516-552) — the SAME strings + the 3000-divisor cost meter.
      const pupg = sel.upg;
      cost = String(pupg.nCost);
      costVal = Math.trunc((pupg.nCost * 100) / 3000);
      costLabelShown = true;
      name = pupg.szName;
      description = pupg.szDescription;
      prerequisites = pupg.szPrerequisites;
      prerequisitesShown = true;
      researchShown = !sel.fDisabled; // hide "Research" if the upgrade is disabled
    }

    const info: BuildPanelInfo = {
      name,
      meterMax: PipMeterCount(mw, 100, pw),
      cost,
      costMeter: PipMeterCount(mw, costVal, pw),
      // research has NO armor/damage/speed/range/power rows (E-R3) — leave them empty/zero.
      armor: '',
      armorMeter: 0,
      speed: '',
      speedMeter: 0,
      range: '',
      rangeMeter: 0,
      damage: { infantry: 0, vehicle: 0, structure: 0 },
      description,
      // the research-only rows.
      prerequisites,
      costLabelShown,
      prerequisitesShown,
    };

    return {
      variant: 'research',
      heading,
      items,
      info,
      limitReached: false, // no limit on FORM E
      orderShown: researchShown, // "Research" button (kidcOk) shown
      cancelOrderShown: false, // no Cancel Order on FORM E
      emptyState: this.fEmpty,
    };
  }

  /** tap an upgrade by upgm — single-selection, faithfully mirroring the canvas list tap
   *  (OnControlSelected(kidcList) → UpdateUpgradeInfo). Like FORM C there is no tap-on-selected
   *  order — Research is a separate button; the info refresh is computed in BuildModel. */
  DomSelect(upgm: number): void {
    let found = false;
    for (const it of this.items) if (it.pv === upgm) found = true;
    if (!found) return;
    for (const it of this.items) it.fSelected = it.pv === upgm;
    this.selUpgm = upgm;
  }

  /** "Research" — the SAME OnControlSelected(kidcOk) the canvas button press runs: end the
   *  form with the selected upgrade's upgm. RETURNS the upgm (or null when nothing is selected)
   *  so the BuildUI ResearchGob continuation can enqueue kmidUpgradeCommand (E-R5) — this form
   *  ENQUEUES NOTHING (U1). */
  DomResearch(): number | null {
    const sel = this.selectedItem();
    if (sel === null) return null;
    const upgm = sel.upg.upgm;
    this.EndForm(); // the canvas OnControlSelected(kidcOk) end-result
    return upgm;
  }
}

//
// StructureBuildForm (game/Headquarters.cpp:5-24 + 315-532)
//

// canvas-removal R3 step (i): StructureBuildForm is no longer `extends Form`. It holds the
// buildable-structure list as plain BuildItemRec records (`pv` = ut) + the selection index +
// m_fLimitReached + the explicit "Build" (kidcOk) visibility flag — and tracks them with the
// EXACT canvas Show(kidcOk) state machine (SetOwner / OnControlNotify / UpdateStructureInfo,
// Headquarters.cpp:318-507) so BuildModel is byte-identical to the old control round-trip.
export class StructureBuildForm {
  m_pgobOwner: BuilderGob | null = null;
  m_fLimitReached = false;
  private readonly ui: BuildUI;
  private items: BuildItemRec[] = [];
  private iliSel = -1; // the selected list index (-1 = none), the BuildListControl state
  private buildShown = false; // the kidcOk "Build" button visibility (the canvas Show(kidcOk))

  constructor(ui: BuildUI) {
    this.ui = ui;
  }

  // ht.h (Headquarters.cpp:8-10)
  GetOwner(): BuilderGob | null {
    return this.m_pgobOwner;
  }

  // the currently-selected list record (the canvas GetSelectedItem), or null.
  private selectedItem(): BuildItemRec | null {
    return this.iliSel >= 0 && this.iliSel < this.items.length ? this.items[this.iliSel] : null;
  }

  // game/Headquarters.cpp:318-386 — populate the buildable-structure list from the sim.
  SetOwner(pgobOwner: BuilderGob): void {
    const ctx = this.ui.deps.ctx;
    this.m_pgobOwner = pgobOwner;
    this.items = [];

    // Initialize list with available structure types.
    const pplr = pgobOwner.GetOwner() as Player;
    const umOwned = ((pplr.GetUnitMask() & kumStructures) | pplr.GetUpgrades()) >>> 0;
    const pbldrc = pgobOwner.GetConsts() as BuilderConsts;
    const umAllowed = (pbldrc.umCanBuild & (ctx.fGodMode ? kumAll : pplr.GetAllowedUnits())) >>> 0;
    const upgmOwned = pplr.GetUpgradeMask();
    for (let i = 0; i < kutMax; i++) {
      const puntc = ctx.unitConsts[i];

      // Does this player have what it takes to build this structure/unit?
      let fDisabled = true;
      if (
        ctx.fGodMode ||
        ((umOwned & puntc.umPrerequisites) >>> 0 === puntc.umPrerequisites &&
          (upgmOwned & puntc.upgmPrerequisites) >>> 0 === puntc.upgmPrerequisites)
      )
        fDisabled = false;

      // Yep, is this one of the types this build form allows?
      if (puntc.um & umAllowed) {
        const panid = this.ui.deps.gctx.anidFor(puntc.szAniName);
        const nStripIcon = panid !== null ? panid.GetStripIndex('icon') : -1;
        if (nStripIcon !== -1) {
          this.items.push({
            pv: puntc.ut,
            panid: panid!,
            nStrip: nStripIcon,
            nFrame: 0,
            fDisabled,
            fSelected: false,
          });
        }
      }
    }

    // Select the first unit by default (BuildListControl.Select(GetLastSelection())). The C++
    // ListControl.Select(N, fOnly=false) (misccontrols.cpp:1474-1542) does NOT clear/pin: it
    // sets fSelected on the Nth item IFF N is in range, else selects NOTHING (no item selected).
    const sel = this.m_pgobOwner.GetLastSelection();
    this.iliSel = sel >= 0 && sel < this.items.length ? sel : -1;
    for (let i = 0; i < this.items.length; i++) this.items[i].fSelected = i === this.iliSel;

    // NB: the gold SetOwner does NOT reset the "Build" button (kidcOk) to visible here —
    // kidcOk is a persisted control on the reused-singleton form, so its visibility CARRIES
    // OVER from the prior open and is touched ONLY by the stale-`m_fLimitReached`-gated
    // structureInfoSelect (below) + the `.Show(false)` limit/no-selection arms. `buildShown`
    // mirrors that persisted state (field-init = forms.ini default; structureInfoSelect sets it on
    // every below-limit open with a selection). An unconditional `buildShown = true` here would
    // break the preserved stale-`m_fLimitReached` bug: reopening below-limit AFTER an at-limit
    // episode must keep Build HIDDEN (the gold's behavior), because the stale m_fLimitReached=true
    // makes structureInfoSelect skip its Show(!fDisabled), leaving Build at the prior at-limit's
    // Show(false). (BuildModel reads buildShown back per poll WITHOUT re-applying the side effect.)

    // BuildListControl.Select(N) (when N selected an item) ran OnSelect(knSelUpInside) →
    // OnControlSelected(kidcList) → UpdateStructureInfo, whose `!m_fLimitReached` guard reads
    // the PRIOR open's m_fLimitReached (this is a reused singleton; the limit check below
    // resets it AFTER). Match the C++ ORDER exactly: run the initial selection's
    // UpdateStructureInfo side effect FIRST (against the stale m_fLimitReached), then the
    // limit/no-selection arms (which re-set m_fLimitReached + re-hide Build).
    if (this.iliSel !== -1) this.structureInfoSelect(this.items[this.iliSel]);

    // If we're at the structure limit, hide the order button + show the limit reached text.
    if (ctx.gobm.IsBelowLimit(knLimitStruct, this.m_pgobOwner.GetOwner() as Player)) {
      this.m_fLimitReached = false;
    } else {
      this.m_fLimitReached = true;
      this.buildShown = false; // Build.Show(false)
    }

    if (!this.m_fLimitReached && this.iliSel === -1) {
      this.buildShown = false; // Build.Show(false)
    }
  }

  // game/Headquarters.cpp:388-471 — the selected-structure stat block (computed DIRECTLY,
  // returning the SAME strings/meter values the canvas UpdateStructureInfo wrote). This is the
  // PURE display computation (deterministic from the sim) — the gold's per-poll BuildModel reads
  // these labels/meters back WITHOUT re-running the side-effectful UpdateStructureInfo, so this
  // must stay side-effect-free. The `!m_fLimitReached → Build.Show(!fDisabled)` side effect on
  // buildShown is applied SEPARATELY (structureInfoSelect) ONLY at the canvas UpdateStructureInfo
  // call sites (SetOwner's initial Select + the DomSelect tap) — NOT here. Re-running it per poll
  // would clobber the persisted (reused-singleton) buildShown after the limit arm cleared
  // m_fLimitReached, breaking the stale-m_fLimitReached preserved bug (Build must stay HIDDEN on a
  // below-limit reopen AFTER an at-limit episode — the gold's persisted-kidcOk behavior).
  private structureInfo(pli: BuildItemRec): BuildPanelInfo {
    const ctx = this.ui.deps.ctx;
    const pstruc = ctx.unitConsts[pli.pv] as StructConsts;
    const mw = this.ui.meterWidthPx;
    const pw = this.ui.pipWidthPx;

    // Cost
    const cost = String(pstruc.GetCost());
    const costVal = Math.trunc((pstruc.GetCost() * 100) / this.ui.GetUnitCostMax());

    // Name
    const name = pstruc.szLongName.toUpperCase(); // HtStrupr

    // Power Demand (scaled by gnPowerSupplyMax so supply/demand share the scale)
    const powerDemand = String(pstruc.nPowerDemand);
    const powerDemandVal = Math.trunc((pstruc.nPowerDemand * 100) / this.ui.gnPowerSupplyMax);

    // Power Supply
    const powerSupply = String(pstruc.nPowerSupply);
    const powerSupplyVal = Math.trunc((pstruc.nPowerSupply * 100) / this.ui.gnPowerSupplyMax);

    // Armor
    const fxOneThirdRange = divfx(
      subfx(this.ui.gfxStructureArmorStrengthMax, this.ui.gfxStructureArmorStrengthMin),
      itofx(3),
    );
    let armor = 'MEDIUM';
    if (pstruc.GetArmorStrength() === 0) armor = 'NONE';
    else if (
      pstruc.GetArmorStrength() < addfx(this.ui.gfxStructureArmorStrengthMin, fxOneThirdRange)
    )
      armor = 'LIGHT';
    else if (
      pstruc.GetArmorStrength() >= subfx(this.ui.gfxStructureArmorStrengthMax, fxOneThirdRange)
    )
      armor = 'HEAVY';
    const armorVal = Math.trunc(
      (fxtoi(pstruc.GetArmorStrength()) * 100) / fxtoi(this.ui.gfxStructureArmorStrengthMax),
    );

    // Description (or the prerequisite list when disabled)
    const description = pli.fDisabled
      ? `This building requires: ${GetPrerequisiteString(pstruc, ctx)}.`
      : (pstruc.szDescription ?? '');

    return {
      name,
      meterMax: PipMeterCount(mw, 100, pw),
      cost,
      costMeter: PipMeterCount(mw, costVal, pw),
      armor,
      armorMeter: PipMeterCount(mw, armorVal, pw),
      // structures have no SPEED/RANGE — leave the unit-variant fields empty.
      speed: '',
      speedMeter: 0,
      range: '',
      rangeMeter: 0,
      // P DEMAND / P SUPPLY (the structure rows).
      powerDemand,
      powerDemandMeter: PipMeterCount(mw, powerDemandVal, pw),
      powerSupply,
      powerSupplyMeter: PipMeterCount(mw, powerSupplyVal, pw),
      // the DAMAGEMETER pip counts (the SAME DrawPips math; towers get pips, the rest zero).
      damage: {
        infantry: DamagePipCount(pstruc.nInfantryDamage, pstruc.ctFiringRate),
        vehicle: DamagePipCount(pstruc.nVehicleDamage, pstruc.ctFiringRate),
        structure: DamagePipCount(pstruc.nStructureDamage, pstruc.ctFiringRate),
      },
      description,
    };
  }

  // game/Headquarters.cpp:467-469 — the canvas UpdateStructureInfo's `!m_fLimitReached →
  // Build.Show(!fDisabled)` SIDE EFFECT. Runs ONLY at the canvas UpdateStructureInfo call sites
  // (SetOwner's initial Select + the DomSelect tap), reading the stale-on-entry m_fLimitReached
  // — exactly like the gold, where this gates off the PRIOR open's limit state (reused singleton).
  private structureInfoSelect(pli: BuildItemRec): void {
    this.structureInfo(pli); // (the pure stat-block compute the canvas wrote into the controls)
    // Hide the "Build" button if this structure is disabled (only when not at the limit).
    if (!this.m_fLimitReached) this.buildShown = !pli.fDisabled;
  }

  // game/Headquarters.cpp:473-479 — SetLastSelection + clear owner.
  EndForm(): void {
    if (this.m_pgobOwner !== null) this.m_pgobOwner.m_iLastSelection = this.iliSel;
    this.m_pgobOwner = null;
  }

  // ── Phase 5 — build/order STEP 3, FORM C ────────────────────────────────────
  // The DOM STRUCTURE carousel reads a pure model off THIS form's plain state and routes
  // selection/Build back through it — the gpfrmPlace handoff stays in BuildUI (the form
  // builds NO Message — C-R10/C-R11/U1). cyItem=18, P DEMAND/P SUPPLY, no overlays, Build/Close.

  GetDomOwner(): BuilderGob | null {
    return this.m_pgobOwner;
  }

  /** a pure read-model of the structure carousel at this instant — the SAME list/stat state
   *  the canvas form computed (SetOwner population + UpdateStructureInfo), built DIRECTLY from
   *  the sim. No queue count / build progress (C-R5); P DEMAND / P SUPPLY instead of SPEED /
   *  RANGE (C-R6). DISPLAY-only. */
  BuildModel(heading: string): BuildPanelModel {
    const side = this.ui.deps.ctx.plrLocal!.GetSide();

    const items: BuildPanelItem[] = [];
    for (const it of this.items) {
      items.push({
        ut: it.pv,
        icon: ExtractListIcon(it, side),
        disabled: it.fDisabled,
        selected: it.fSelected,
        queueCount: 0,
        buildProgress: null,
      });
    }

    // the stat block — the SAME strings/meters UpdateStructureInfo set for the selection.
    // structureInfo is the PURE display compute (no buildShown side effect) — the gold's per-poll
    // BuildModel reads the labels/meters back without re-running UpdateStructureInfo, and reads
    // kidcOk's persisted visibility flag. buildShown is the persisted (reused-singleton) state
    // last set by SetOwner/DomSelect, so it survives a below-limit reopen after an at-limit episode.
    const pli = this.selectedItem();
    const info: BuildPanelInfo | null = pli !== null ? this.structureInfo(pli) : null;

    return {
      variant: 'structure',
      heading,
      items,
      info,
      limitReached: this.m_fLimitReached,
      orderShown: this.buildShown, // "Build" button (kidcOk) shown (persisted, read back)
      cancelOrderShown: false, // no Cancel Order on FORM C
    };
  }

  /** tap a structure by ut — single-selection + UpdateStructureInfo, faithfully mirroring the
   *  canvas list tap (OnControlNotify(kidcList) → UpdateStructureInfo). Unlike FORM D there is
   *  NO tap-on-selected order — the structure panel only selects (Build is a separate button). */
  DomSelect(ut: number): void {
    let iliSel = -1;
    for (let i = 0; i < this.items.length; i++)
      if (this.items[i].pv === ut) {
        iliSel = i;
        break;
      }
    if (iliSel < 0) return;
    for (let i = 0; i < this.items.length; i++) this.items[i].fSelected = i === iliSel;
    this.iliSel = iliSel;
    // refresh the stat block + the Build-button visibility (the canvas UpdateStructureInfo hides
    // Build if disabled, when not at the limit). This is the canvas list-tap call site, so it
    // runs the SIDE-EFFECTFUL select variant (BuildModel reads the persisted buildShown back
    // without re-applying the side effect — matching the gold's persisted-kidcOk semantics).
    this.structureInfoSelect(this.items[iliSel]);
  }

  /** "Build" — the SAME OnControlSelected(kidcOk) the canvas button press runs: end the form
   *  with the selected structure's gob TYPE (gt). RETURNS the gt (or null when nothing is
   *  selected) so the BuildUI continuation can run the gpfrmPlace placement handoff (C-R10) —
   *  this form ENQUEUES NOTHING (C-R11/U1). */
  DomBuild(): number | null {
    const pli = this.selectedItem();
    if (pli === null) return null;
    const gt = this.ui.deps.ctx.unitConsts[pli.pv].gt;
    this.EndForm(); // saves m_iLastSelection (C-R9)
    return gt;
  }
}

//
// PlaceStructureForm (game/Headquarters.cpp:534-1132)
//

// game/Headquarters.cpp:707-708
const kwcButtonInset = 56;
const kwcButtonWidth = 50 + kwcButtonInset;

// canvas-removal R3 step (i): the placement Ok/Cancel buttons are plain records (no ButtonControl).
// Each carries the bitmaps (okup/okdown, cancelup/canceldown) + the scaled form-relative rect +
// the kfCtl* flag word. ButtonControl finger-hit-test inflated the rect by 20 (form.cpp:1013-1019).
class PlaceButtonRec {
  readonly idc: number;
  m_rc = new Rect();
  m_wf = kfCtlVisible;
  m_ptbmUp: TBitmap | null = null;
  m_ptbmDown: TBitmap | null = null;
  /** Control::Invalidate's reach (form.cpp:1085-1096) — damages this button's
   *  on-screen rect through the owning placement layer (offset by the form
   *  origin there). Wired in PlaceStructureForm.Init. The control-tree removal
   *  dropped the ButtonControl invalidations SetPosition/Show/SetRect bracketed
   *  their mutations with; the SDL-era full repaint hid that, but under scoped
   *  invalidation a moved placement indicator trailed stale ✓/✗ button pixels
   *  (the buttons hang OUTSIDE the invalidated structure-outline rect). */
  invalidate: (() => void) | null = null;
  constructor(idc: number) {
    this.idc = idc;
  }
  // form.cpp:1085-1088 — invisible controls don't damage
  private Invalidate(): void {
    if (!(this.m_wf & kfCtlVisible)) return;
    this.invalidate?.();
  }
  GetFlags(): number {
    return this.m_wf;
  }
  // form.cpp:1047-1061 Show — invalidate on the visible side of each transition
  Show(fShow: boolean): void {
    if (fShow) {
      if (this.m_wf & kfCtlVisible) return;
      this.m_wf |= kfCtlVisible;
      this.Invalidate();
    } else {
      if (!(this.m_wf & kfCtlVisible)) return;
      this.Invalidate();
      this.m_wf &= ~kfCtlVisible;
    }
  }
  GetRect(prc: Rect): void {
    prc.SetRc(this.m_rc);
  }
  // form.cpp:1028-1035 SetRect (the fCompareRect=false arm)
  SetRect(prc: Rect): void {
    this.Invalidate();
    this.m_rc.SetRc(prc);
    this.Invalidate();
  }
  // form.cpp:1037-1045 SetPosition.
  SetPosition(x: number, y: number): void {
    if (this.m_rc.left === x && this.m_rc.top === y) return;
    this.Invalidate();
    this.m_rc.Offset(x - this.m_rc.left, y - this.m_rc.top);
    this.Invalidate();
  }
  // form.cpp:1013-1019 GetFingerRect — the rect inflated by 20.
  GetFingerRect(prc: Rect): void {
    prc.SetRc(this.m_rc);
    prc.Inflate(20, 20);
  }
  Width(): number {
    return this.m_rc.Width();
  }
  Height(): number {
    return this.m_rc.Height();
  }
}

// canvas-removal R3 step (i): PlaceStructureForm is no longer `extends Form` — it is a PaintLayer
// (like SimUIForm/InputUIForm), AddLayer'd onto gpfrmmSim. The FormMgr's incremental paint/scroll
// + pen-event COOKING survive R3 (only the Form base + control tree are removed), so the layer
// keeps the EXACT input behaviour (OnHitTest competes in CookPenEvent's top-down walk; EventProc →
// OnPenEvent) and the EXACT placement render (OnPaintSimUI/OnPaintControlsSimUI, still called by
// SimUIForm via the GetPlaceForm host seam) — with plain Ok/Cancel records instead of controls.
export class PlaceStructureForm implements PaintLayer {
  m_pgobOwner: BuilderGob | null = null;
  m_pstruc: StructConsts | null = null;
  m_tx = 0;
  m_ty = 0;
  m_wxPen = 0;
  m_wyPen = 0;
  m_wxDragStart = 0;
  m_wyDragStart = 0;
  m_txStart = 0;
  m_tyStart = 0;
  m_fDragging: boolean;
  m_fPassOnInput: boolean;
  private readonly ui: BuildUI;
  private m_tmr = new CallbackTimer((t) => this.OnTimer(t));

  // PaintLayer identity/geometry (the engine reads these off m_apfrm).
  m_pfrmm: FormMgr | null = null;
  m_rc = new Rect();
  m_idf = kidfPlaceStructure; // the cooked-event form id (CookPenEvent capture key)
  m_wf = 0; // kfFrmVisible (set on AddLayer/Init) | kfFrmTranslucent

  // the Ok / Cancel button records (the placement control tree) — built in Init.
  private m_btnOk = new PlaceButtonRec(kidcOk);
  private m_btnCancel = new PlaceButtonRec(kidcCancel);
  // the button-press capture state (replaces Form's m_pctlCapture for the 2 buttons):
  // the captured button (kidcOk/kidcCancel) or 0, and whether the pen is currently inside it.
  private m_idcCapture = 0;
  private m_fPenInside = false;

  // game/Headquarters.cpp:538-544 (ctor)
  constructor(ui: BuildUI) {
    this.ui = ui;
    this.m_pgobOwner = null;
    this.m_fDragging = false;
    this.m_fPassOnInput = false;
    this.m_wf |= kfFrmTranslucent;
  }

  // Init replaces Form.Init's control-tree build: load the Ok/Cancel bitmaps + scaled rects from
  // the placement form section (1018) — the SAME geometry/bitmaps ButtonControl.Init produced.
  Init(pfrmm: FormMgr, section: import('../../../sim/src/data/ini.ts').IniSection): boolean {
    this.m_pfrmm = pfrmm;
    const host = pfrmm.host;
    const div10 = host.gcxFullScreenFormDiv10;
    for (const p of section.props) {
      if (p.key !== 'BUTTON') continue;
      const r = IniScanf(p.value, '%d (%d %d %d %d) "%s" %d %s %s %s');
      const idc = r.values[0] as number;
      const rec = idc === kidcOk ? this.m_btnOk : idc === kidcCancel ? this.m_btnCancel : null;
      if (rec === null) continue;
      const x = PcFromFc(r.values[1] as number, div10);
      const y = PcFromFc(r.values[2] as number, div10);
      const cxFc = r.values[3] as number,
        cyFc = r.values[4] as number;
      const cx = PcFromFc(cxFc, div10);
      const cy = PcFromFc(cyFc, div10);
      // tail: "label" nfnt center? up.tbm down.tbm — the placement buttons are nocenter up/down.
      const tail: string[] = [];
      for (let i = 7; i < r.values.length; i++) tail.push(r.values[i] as string);
      const szUp = tail[1] ?? '',
        szDown = tail[2] ?? '';
      rec.m_rc.Set(x, y, x + cx, y + cy);
      if (szUp !== '') rec.m_ptbmUp = host.createTBitmap(szUp);
      if (szDown !== '') rec.m_ptbmDown = host.createTBitmap(szDown);
      // ButtonControl.InitParts: resize to max(up,down) bitmap size.
      if (rec.m_ptbmUp !== null && rec.m_ptbmDown !== null) {
        const s1 = rec.m_ptbmUp.GetSize();
        const s2 = rec.m_ptbmDown.GetSize();
        const bcx = Math.max(s1.cx, s2.cx),
          bcy = Math.max(s1.cy, s2.cy);
        if (bcx !== 0 && bcy !== 0) {
          rec.m_rc.right = rec.m_rc.left + bcx;
          rec.m_rc.bottom = rec.m_rc.top + bcy;
        }
      }
    }
    // Control::Invalidate (form.cpp:1085-1096): damage the button's rect offset by
    // the FORM origin, through the FormMgr — wired here because the records need
    // the live layer rect (this.m_rc) at call time.
    const btnInvalidate = (rec: PlaceButtonRec) => (): void => {
      const rc = new Rect();
      rc.SetRc(rec.m_rc);
      rc.Offset(this.m_rc.left, this.m_rc.top);
      this.InvalidateRect(rc);
    };
    this.m_btnOk.invalidate = btnInvalidate(this.m_btnOk);
    this.m_btnCancel.invalidate = btnInvalidate(this.m_btnCancel);
    return true;
  }

  GetId(): number {
    return this.m_idf;
  }
  GetFlags(): number {
    return this.m_wf;
  }
  GetFormMgr(): FormMgr | null {
    return this.m_pfrmm;
  }
  private GetControlRec(idc: number): PlaceButtonRec {
    return idc === kidcOk ? this.m_btnOk : this.m_btnCancel;
  }

  // ht.h:7547-7548
  SetPassOnInput(fPassOn: boolean): void {
    this.m_fPassOnInput = fPassOn;
  }
  IsBusy(): boolean {
    return this.m_pgobOwner !== null;
  }

  // ht.h:7542-7544
  GetOwner(): BuilderGob | null {
    return this.m_pgobOwner;
  }

  // game/Headquarters.cpp:546-556 — resize the placement form to cover the whole display
  OnDisplayResize(): void {
    const pbm = this.m_pfrmm!.GetDib();
    const siz = pbm.GetSize();
    const rcForm = new Rect();
    rcForm.Set(0, 0, siz.cx, siz.cy);
    this.SetRect(rcForm);
  }

  // a button's hit-test (form.cpp:972-1001 Control.OnHitTest): finger → the inflated finger rect,
  // else the inner rect; returns >=0 (the distance) on a hit, -1 otherwise.
  private btnHitTest(rec: PlaceButtonRec, pevt: Event): number {
    if (!(rec.m_wf & kfCtlVisible)) return -1;
    const x = pevt.x - this.m_rc.left,
      y = pevt.y - this.m_rc.top;
    const nDist = rec.m_rc.GetDistance(x, y);
    if (pevt.ff & kfEvtFinger) {
      const rcT = new Rect();
      rec.GetFingerRect(rcT);
      if (rcT.PtIn(x, y)) return nDist;
    } else if (nDist === 0) return 0;
    return -1;
  }

  // game/Headquarters.cpp:558-602
  OnHitTest(pevt: Event): boolean {
    // If not passing on input to the form below, do normal form processing (the layer's rect).
    if (!this.m_fPassOnInput) {
      if (!(this.m_wf & kfFrmVisible)) return false;
      // finger input may hit a control outside the form rect first (the 20px finger rect).
      if (pevt.ff & kfEvtFinger) {
        if (
          this.btnHitTest(this.m_btnOk, pevt) >= 0 ||
          this.btnHitTest(this.m_btnCancel, pevt) >= 0
        )
          return true;
      }
      return this.m_rc.PtIn(pevt.x, pevt.y);
    }

    // Nothing goes to an invisible form.
    if (!(this.m_wf & kfFrmVisible)) return false;

    // If the form has the mouse captured it should get input.
    if (this.HasCapture()) return true;

    // Otherwise, input not on the placement form is passed on (the SimUI form moves the map
    // with finger input). The buttons claim their hits first.
    if (this.btnHitTest(this.m_btnOk, pevt) >= 0 || this.btnHitTest(this.m_btnCancel, pevt) >= 0)
      return true;

    // On the placement form somewhere else?
    const wptView = { wx: 0, wy: 0 };
    this.ui.deps.sui.GetViewPos(wptView);
    const rcInside = new Rect();
    const rcOutside = new Rect();
    this.GetSubRects(wptView.wx, wptView.wy, rcInside, rcOutside);
    if (rcOutside.PtIn(pevt.x, pevt.y)) return true;

    // Pass it on to next form.
    return false;
  }

  // the FormMgr form-level capture (form.cpp:2801-2803 HasCapture).
  HasCapture(): boolean {
    return this.m_pfrmm!.GetFormCapture() === this;
  }

  // game/Headquarters.cpp:604-630
  OnControlSelected(idc: number): void {
    this.ui.deps.gtimm.RemoveTimer(this.m_tmr);

    if (idc === kidcCancel) {
      this.ui.deps.gpfrmmSim.RemoveForm(this);
      this.m_pgobOwner = null;
      return;
    }

    const msg = newMessage();
    msg.smidSender = ksmidNull;
    msg.mid = kmidBuildOtherCommand;
    msg.BuildOtherCommand.ut = this.m_pstruc!.ut;
    msg.BuildOtherCommand.wpt.wx = WcFromTc(this.m_tx);
    msg.BuildOtherCommand.wpt.wy = WcFromTc(this.m_ty);
    msg.smidReceiver = this.m_pgobOwner!.GetId();
    this.ui.deps.ctx.cmdq.Enqueue(msg);

    if (this.m_pgobOwner!.GetOwner() === this.ui.deps.ctx.plrLocal)
      this.ui.deps.PlaySfx((this.m_pgobOwner!.GetConsts() as BuilderConsts).sfxUnitBuild);

    this.ui.deps.gpfrmmSim.RemoveForm(this);

    this.m_pgobOwner = null;
  }

  // game/Headquarters.cpp:632-705
  SetOwner(pgobOwner: BuilderGob, ut: number): void {
    const deps = this.ui.deps;
    deps.gtimm.AddTimer(this.m_tmr, kctMapScrollRate);

    // form is loaded as the size of the whole playfield
    // draw the structure placement indicator near the HQ
    this.m_pgobOwner = pgobOwner;
    this.m_pstruc = deps.ctx.unitConsts[ut] as StructConsts;
    const wptT = { wx: 0, wy: 0 };
    this.m_pgobOwner.GetPosition(wptT);

    const wptView = { wx: 0, wy: 0 };
    deps.sui.GetViewPos(wptView);
    const wxView = wptView.wx;
    const wyView = wptView.wy;
    const sizPlayfield = deps.GetPlayfieldSize();
    const c = deps.gctx.coords;
    let wxViewMax = c.WcFromPc(sizPlayfield.cx) + wxView;
    let wyViewMax = c.WcFromPc(sizPlayfield.cy) + wyView;

    // be consistent about appearing below the HQ unless we will be hidden

    const pstrucOwner = pgobOwner.GetConsts() as StructConsts;
    let wxTarget = wptT.wx;
    let wyTarget = wptT.wy + WcFromTc(pstrucOwner.ctyReserve);

    // now make sure we're onscreen

    wxViewMax -= WcFromTc(this.m_pstruc.ctxReserve);
    if (wxTarget < wxView) wxTarget = wxView;
    else if (wxTarget > wxViewMax) wxTarget = wxViewMax;
    this.m_tx = this.m_txStart = TcFromWc(wxTarget);

    if (wyTarget < wyView) {
      wyTarget = wyView;
    } else {
      // watch out for being behind the minimap as well on the screen

      const rc = deps.getMiniMapRect();
      if (
        rc !== null &&
        wyTarget > c.WcFromUpc(rc.top) + wyView - WcFromTc(this.m_pstruc.ctyReserve)
      ) {
        const wyMiniMap = c.WcFromUpc(rc.top) + wyView;
        // below the top of the mini map - check the horizontal

        const wxMiniMap = c.WcFromUpc(rc.left) + wxView;
        if (wxTarget + WcFromTc(this.m_pstruc.ctxReserve) > wxMiniMap) {
          // behind the mini map, go above

          wyTarget = wyMiniMap - WcFromTc(this.m_pstruc.ctyReserve);
        } else {
          // not behind minimap, just watch screen bottom

          wyViewMax -= WcFromTc(this.m_pstruc.ctyReserve);
          if (wyTarget > wyViewMax) wyTarget = wyViewMax;
        }
      }
    }

    this.m_ty = this.m_tyStart = TcFromWc(wyTarget);

    this.m_wxPen = this.m_wxDragStart = 0;
    this.m_wyPen = this.m_wyDragStart = 0;

    // use UpdatePlacementIndicator to get the buttons initialized to the
    // correct location

    this.UpdatePlacementIndicator(wxView, wyView, wxView, wyView);
  }

  // game/Headquarters.cpp:710-739
  GetSubRects(wx: number, wy: number, prcInside: Rect, prcOutside: Rect): void {
    const c = this.ui.deps.gctx.coords;
    const wrcInside = {
      left: WcFromTc(this.m_tx),
      top: WcFromTc(this.m_ty),
      right: WcFromTc(this.m_tx + this.m_pstruc!.ctxReserve),
      bottom: WcFromTc(this.m_ty + this.m_pstruc!.ctyReserve),
    };
    const wrcOutside = { ...wrcInside };

    // (the IPHONE/SDL widen arm — the web build defines SDL)
    if (wrcOutside.right - wrcOutside.left < WcFromTc(3)) {
      const dw = Math.trunc((WcFromTc(3) - (wrcOutside.right - wrcOutside.left)) / 2);
      wrcOutside.left -= dw;
      wrcOutside.right += dw;
    }

    const siz = this.ui.deps.GetMapSizePx();
    if (wrcOutside.left < kwcButtonWidth) {
      wrcOutside.left = kwcButtonWidth;
    }
    if (wrcOutside.right > c.WcFromUpc(siz.cx) - kwcButtonWidth) {
      wrcOutside.right = c.WcFromUpc(siz.cx) - kwcButtonWidth;
    }

    prcInside.Set(
      c.PcFromWc(wrcInside.left - wx),
      c.PcFromWc(wrcInside.top - wy),
      c.PcFromWc(wrcInside.right - wx),
      c.PcFromWc(wrcInside.bottom - wy),
    );
    prcOutside.Set(
      c.PcFromWc(wrcOutside.left - wx),
      c.PcFromWc(wrcOutside.top - wy),
      c.PcFromWc(wrcOutside.right - wx),
      c.PcFromWc(wrcOutside.bottom - wy),
    );
  }

  // game/Headquarters.cpp:741-823 — do the math to move the hash marks that
  // denote where your building might go but keep it tile-aligned
  UpdatePlacementIndicator(
    wxViewStart: number,
    wyViewStart: number,
    wxView: number,
    wyView: number,
  ): void {
    const deps = this.ui.deps;
    const c = deps.gctx.coords;

    // Invalidate our old location

    const rcInside = new Rect();
    const rcOutside = new Rect();
    this.GetSubRects(wxViewStart, wyViewStart, rcInside, rcOutside);
    this.InvalidateRect(rcOutside);

    // always use pen movement from start, incremental pen
    // movements get rounded down to zero when converted to
    // tiles

    this.m_tx = this.m_txStart + TcFromWc(this.m_wxPen) - TcFromWc(this.m_wxDragStart);
    this.m_ty = this.m_tyStart + TcFromWc(this.m_wyPen) - TcFromWc(this.m_wyDragStart);

    // don't let the structure outline go off map at all

    if (this.m_tx < 0) this.m_tx = 0;
    if (this.m_ty < 0) this.m_ty = 0;
    const siz = deps.GetMapSizePx();
    const ctxMap = Math.trunc(siz.cx / c.gcxTile);
    const ctyMap = Math.trunc(siz.cy / c.gcxTile); // gcyTile == gcxTile
    if (this.m_tx + this.m_pstruc!.ctxReserve >= ctxMap)
      this.m_tx = ctxMap - this.m_pstruc!.ctxReserve;
    if (this.m_ty + this.m_pstruc!.ctyReserve >= ctyMap)
      this.m_ty = ctyMap - this.m_pstruc!.ctyReserve;

    // Invalidate our new location

    this.GetSubRects(wxView, wyView, rcInside, rcOutside);
    this.InvalidateRect(rcOutside);

    // Draw 'x', 'check' buttons
    // check gets shown/hidden in DrawPlacementTiles because other gob movement
    // affects whether or not it shows

    let pbtn = this.m_btnCancel;
    const rcCtl = new Rect();
    pbtn.GetRect(rcCtl);
    const y =
      c.PcFromWc(WcFromTc(this.m_ty) - wyView) +
      Math.trunc((this.m_pstruc!.ctyReserve * c.gcxTile - rcCtl.Height()) / 2); // PcFromTc

    pbtn.SetPosition(rcOutside.right - c.PcFromWc(kwcButtonInset), y);
    pbtn = this.m_btnOk;
    pbtn.SetPosition(rcOutside.left - rcCtl.Width() + c.PcFromWc(kwcButtonInset), y);

    // See if it's valid

    let fValid = true;
    const fPlacementValid = GobMgrIsStructurePlacementValid(
      this.ui,
      this.m_pstruc!,
      this.m_tx,
      this.m_ty,
      this.m_pgobOwner!.GetOwner() as Player,
    );

    // Show / hide ok button?

    const pfogm = deps.ctx.fogm!;

    for (let ty = this.m_ty; ty < this.m_ty + this.m_pstruc!.ctyReserve; ty++) {
      for (let tx = this.m_tx; tx < this.m_tx + this.m_pstruc!.ctxReserve; tx++) {
        if (
          fPlacementValid &&
          IsTileFree(deps.ctx, tx, ty, kbfReserved | kbfStructure).free &&
          pfogm.GetGalaxite(tx, ty) === 0
        ) {
          continue;
        } else {
          fValid = false;
          break;
        }
      }
    }

    // Show ok button if valid

    pbtn = this.m_btnOk;
    pbtn.Show(fValid || deps.ctx.fGodMode);
  }

  // game/Headquarters.cpp:825-853 — check to see if the input is over a
  // control, while making sure the control doesn't steal input from the hash
  // mark area
  FingerHitTest(pevt: Event): boolean {
    const rcFinger = new Rect();
    const rcT = new Rect();

    // kidcOk is on the left

    let pctl = this.m_btnOk;
    pctl.GetFingerRect(rcFinger);
    pctl.GetRect(rcT);
    rcFinger.right = rcT.right;
    if (rcFinger.PtIn(pevt.x - this.m_rc.left, pevt.y - this.m_rc.top)) {
      return true;
    }

    // kidcCancel is on the right

    pctl = this.m_btnCancel;
    pctl.GetFingerRect(rcFinger);
    pctl.GetRect(rcT);
    rcFinger.left = rcT.left;
    if (rcFinger.PtIn(pevt.x - this.m_rc.left, pevt.y - this.m_rc.top)) {
      return true;
    }

    return false;
  }

  // the button-press capture state machine (replaces Form.OnPenEvent's m_pctlCapture +
  // Control.OnSelect for the 2 buttons): on penDown over a VISIBLE button → capture it; on penUp
  // INSIDE the captured button (dw===0) → fire OnControlSelected(idc); penUp elsewhere → release.
  // Returns true if a button consumed the event. game/form.cpp:388-466 + misccontrols button OnSelect.
  private buttonPen(pevt: Event): boolean {
    if (this.m_idcCapture !== 0) {
      const rec = this.GetControlRec(this.m_idcCapture);
      const inside = this.btnHitTest(rec, pevt) >= 0;
      switch (pevt.eType) {
        case penUpEvent: {
          const idc = this.m_idcCapture;
          this.m_idcCapture = 0;
          this.m_fPenInside = false;
          // NB: NO sfx here — the canvas ButtonControl.OnSelect(knSelUpInside) plays nothing
          // (only knSelDownInside plays ksfxGuiButtonTap, misccontrols.cpp:252-271); the down
          // path below already played it.
          if (inside && pevt.dw === 0) this.OnControlSelected(idc);
          break;
        }
        case penMoveEvent:
          // form.cpp:459-465: on an outside↔inside transition, OnSelect(knSelMoveInside/Outside).
          // ButtonControl.OnSelect plays ksfxGuiButtonTap on knSelMoveInside (the outside→inside
          // re-entry), Invalidate on knSelMoveOutside (misccontrols.cpp:252-271).
          if (this.m_fPenInside !== inside) {
            this.m_fPenInside = inside;
            if (inside) this.ui.deps.PlaySfx(ksfxGuiButtonTap);
          }
          break;
        default:
          break;
      }
      return true;
    }

    if (pevt.eType !== penDownEvent) return false;
    // auto-capture on a pen down over a visible button (the ListControl-style first-crack).
    let hit: PlaceButtonRec | null = null;
    if (this.btnHitTest(this.m_btnOk, pevt) >= 0) hit = this.m_btnOk;
    else if (this.btnHitTest(this.m_btnCancel, pevt) >= 0) hit = this.m_btnCancel;
    if (hit === null) return false;
    this.m_idcCapture = hit.idc;
    this.m_fPenInside = true;
    this.ui.deps.PlaySfx(ksfxGuiButtonTap); // knSelDownInside button tap sfx
    return true;
  }

  // game/Headquarters.cpp:855-945
  OnPenEvent(pevt: Event): boolean {
    // If a button is captured, do regular (button) processing.
    if (this.m_idcCapture !== 0) {
      return this.buttonPen(pevt);
    }

    // If this is finger input, hit test the buttons specially.
    // This is so the controls don't take input meant for the hash mark
    // part of the form (the controls have extra wide finger hit test
    // rects).
    if (pevt.ff & kfEvtFinger) {
      if (pevt.eType === penDownEvent && this.FingerHitTest(pevt)) {
        if (this.buttonPen(pevt)) {
          return true;
        }
      }
    } else {
      // Give the form controls first crack at handling the event (the Ok/Cancel buttons).
      if (this.buttonPen(pevt)) {
        return true;
      }
    }

    // Handle drag-scrolling mode (shift-drag or graffiti-scroll)

    if (this.m_fDragging) {
      switch (pevt.eType) {
        case penDownEvent:
        // Something strange is going on, fall through to penUpEvent to
        // clear dragging flag and complete in-progress drag operation.

        // ...FALL THROUGH...

        case penUpEvent:
          this.m_fDragging = false;

        // ...FALL THROUGH...

        case penMoveEvent: {
          const wptView = { wx: 0, wy: 0 };
          this.ui.deps.sui.GetViewPos(wptView);
          const wxView = wptView.wx;
          const wyView = wptView.wy;
          const c = this.ui.deps.gctx.coords;

          // remember where the pen is for scrolling on update
          // do everything in world coordinates so we don't get confused
          // when the view scrolls

          this.m_wxPen = c.WcFromPc(pevt.x) + wxView;
          this.m_wyPen = c.WcFromPc(pevt.y) + wyView;

          // Pen may move into a graffiti area, but we'll go no farther
          // than the playfield to keep the Placement Indicator at least
          // partly on screen. This won't mess up scrolling because it is
          // triggered by pen position, not by a pen move event.

          const sizPlayfield = this.ui.deps.GetPlayfieldSize();
          const wcxPlayfield = c.WcFromUpc(sizPlayfield.cx);
          const wcyPlayfield = c.WcFromUpc(sizPlayfield.cy);

          if (this.m_wxPen > wxView + wcxPlayfield) this.m_wxPen = wxView + wcxPlayfield;
          if (this.m_wxPen < wxView) this.m_wxPen = wxView;
          if (this.m_wyPen > wyView + wcyPlayfield) this.m_wyPen = wyView + wcyPlayfield;
          if (this.m_wyPen < wyView) this.m_wyPen = wyView;

          // move structure outline, but keep it within the map boundry
          // no change in view

          this.UpdatePlacementIndicator(wxView, wyView, wxView, wyView);
          break;
        }
      }
    } else {
      // do our regular thing

      if (pevt.eType === penDownEvent) {
        this.UpdatePosition(pevt);
        this.m_fDragging = true;
      }
    }

    return true;
  }

  // game/Headquarters.cpp:947-978
  UpdatePosition(pevt: Event): void {
    // If pen down in placement form, drag it without offsetting
    // If pen down outside placement form, center placement for under pen

    const wptView = { wx: 0, wy: 0 };
    this.ui.deps.sui.GetViewPos(wptView);
    const c = this.ui.deps.gctx.coords;
    this.m_wxPen = c.WcFromPc(pevt.x) + wptView.wx;
    this.m_wyPen = c.WcFromPc(pevt.y) + wptView.wy;
    this.m_wxDragStart = this.m_wxPen;
    this.m_wyDragStart = this.m_wyPen;

    const trcT = {
      left: this.m_tx,
      top: this.m_ty,
      right: this.m_tx + this.m_pstruc!.ctxReserve,
      bottom: this.m_ty + this.m_pstruc!.ctyReserve,
    };
    const txPen = TcFromWc(this.m_wxPen);
    const tyPen = TcFromWc(this.m_wyPen);
    if (!(txPen >= trcT.left && txPen < trcT.right && tyPen >= trcT.top && tyPen < trcT.bottom)) {
      // New position for the placement indicator. Center indicator underneath
      // pen

      this.m_txStart = txPen - Math.trunc((this.m_pstruc!.ctxReserve - 1) / 2);
      if (this.m_txStart < 0) this.m_txStart = 0;
      this.m_tyStart = tyPen - Math.trunc((this.m_pstruc!.ctyReserve - 1) / 2);
      if (this.m_tyStart < 0) this.m_tyStart = 0;
    } else {
      // Start from current position

      this.m_txStart = this.m_tx;
      this.m_tyStart = this.m_ty;
    }
  }

  // game/Headquarters.cpp:981-1048 — a real-time (not simulation time) timer
  // started when the placement form is shown, so we can scroll when the form
  // is dragged off-screen
  OnTimer(_tCurrent: number): void {
    const deps = this.ui.deps;
    const c = deps.gctx.coords;

    // get fancy with scrolling. World Coordinates, please
    // imitated from SimUI.cpp::Update

    const wx = this.m_wxPen; // where the pen last moved
    const wy = this.m_wyPen;

    const wptView = { wx: 0, wy: 0 };
    deps.sui.GetViewPos(wptView);
    const wxView = wptView.wx;
    const wyView = wptView.wy;

    const sizPlayfield = deps.GetPlayfieldSize();
    const wcxPlayfield = c.WcFromUpc(sizPlayfield.cx);
    const wcyPlayfield = c.WcFromUpc(sizPlayfield.cy);

    let wxViewNew = wxView;
    let wyViewNew = wyView;

    // is the pen near an edge? if so, scroll

    if (this.m_fDragging) {
      // Screen edge

      if (wx < wxView + kwcScrollBorderSize) {
        wxViewNew -= kwcScrollStepSize;
      } else if (wx > wxView + wcxPlayfield - kwcScrollBorderSize) {
        wxViewNew += kwcScrollStepSize;
      }
      if (wy < wyView + kwcScrollBorderSize) {
        wyViewNew -= kwcScrollStepSize;
      } else if (wy > wyView + wcyPlayfield - kwcScrollBorderSize) {
        wyViewNew += kwcScrollStepSize;
      }
    }

    // if we should scroll: update the view, the position of the pen
    // and the structure outline by the amount we scrolled.
    // setting the viewpos and re-getting it lets the sim
    // deal with map edges for us

    if (wxViewNew !== wxView || wyViewNew !== wyView) deps.sui.SetViewPos(wxViewNew, wyViewNew);

    const wptActual = { wx: 0, wy: 0 };
    deps.sui.GetViewPos(wptActual);

    this.m_wxPen += wptActual.wx - wxView;
    this.m_wyPen += wptActual.wy - wyView;

    this.UpdatePlacementIndicator(wxView, wyView, wptActual.wx, wptActual.wy);

    // Cause a redraw to drag independently from the game rate

    deps.SetRedrawFlags(kfRedrawDirty | kfRedrawBeforeTimer);
  }

  // game/Headquarters.cpp:1050-1068 — we're scrolling: don't let the buttons
  // get left behind
  OnScroll(dx: number, dy: number): void {
    let pbtn = this.m_btnCancel;
    const rcCtl = new Rect();

    // let it invalidate, as long as the PlacementTiles draw on every paint the
    // controls will need to as well.

    pbtn.GetRect(rcCtl);
    rcCtl.Offset(dx, dy);
    pbtn.SetRect(rcCtl);

    pbtn = this.m_btnOk;
    pbtn.GetRect(rcCtl);
    rcCtl.Offset(dx, dy);
    pbtn.SetRect(rcCtl);
  }

  // game/Headquarters.cpp:1070-1125
  OnPaintSimUI(pbm: CpuDibBitmap): void {
    if (this.m_pgobOwner === null) return;

    const deps = this.ui.deps;
    const c = deps.gctx.coords;
    const wptView = { wx: 0, wy: 0 };
    deps.sui.GetViewPos(wptView);
    const rcInside = new Rect();
    const rcOutside = new Rect();
    this.GetSubRects(wptView.wx, wptView.wy, rcInside, rcOutside);

    // If the outside is larger than the inside, draw leader lines from
    // the inside to the edge of the controls, if they are visible.
    // Ok is on the left.

    if (!rcOutside.Equal(rcInside)) {
      // Ok is on the left, Cancel is on the right
      let pctl: PlaceButtonRec = this.m_btnOk;
      if (pctl.GetFlags() & kfCtlVisible) {
        const rcT = new Rect();
        pctl.GetRect(rcT);
        const x = this.m_rc.left + rcT.right;
        const y = this.m_rc.top + rcT.top + Math.trunc(rcT.Height() / 2);
        pbm.Fill(x, y, rcInside.left - x, 1, GetColor(iclr.kiclrWhite));
      }

      // Cancel is on the right

      pctl = this.m_btnCancel;
      if (pctl.GetFlags() & kfCtlVisible) {
        const rcT = new Rect();
        pctl.GetRect(rcT);
        const x = rcInside.right;
        const y = this.m_rc.top + rcT.top + Math.trunc(rcT.Height() / 2);
        pbm.Fill(x, y, this.m_rc.left + rcT.left - x, 1, GetColor(iclr.kiclrWhite));
      }
    }

    const fPlacementValid = GobMgrIsStructurePlacementValid(
      this.ui,
      this.m_pstruc!,
      this.m_tx,
      this.m_ty,
      this.m_pgobOwner.GetOwner() as Player,
    );

    // Draw placement tiles, properly colored

    const pfogm = deps.ctx.fogm!;

    for (let ty = this.m_ty; ty < this.m_ty + this.m_pstruc!.ctyReserve; ty++) {
      for (let tx = this.m_tx; tx < this.m_tx + this.m_pstruc!.ctxReserve; tx++) {
        let ptbm: TBitmap;
        if (
          fPlacementValid &&
          IsTileFree(deps.ctx, tx, ty, kbfReserved | kbfStructure).free &&
          pfogm.GetGalaxite(tx, ty) === 0
        ) {
          ptbm = this.ui.s_ptbmPlacementGood!;
        } else {
          ptbm = this.ui.s_ptbmPlacementBad!;
        }
        ptbm.BltTo(
          pbm,
          tx * c.gcxTile - c.PcFromUwc(wptView.wx),
          ty * c.gcxTile - c.PcFromUwc(wptView.wy),
        );
      }
    }
  }

  // game/Headquarters.cpp:1127-1132 — paint the Ok/Cancel button bitmaps (the canvas
  // Form.OnPaintControls walk over the 2 ButtonControls, reproduced directly: a pressed
  // (captured + pen inside) button draws its down bitmap, else up; ksideNeutral — the
  // placement buttons do NOT use the side1 colors the menu buttons force).
  OnPaintControlsSimUI(pbm: CpuDibBitmap, _pupd: UpdateMap | null): void {
    if (this.m_pgobOwner === null) return;
    for (const rec of [this.m_btnOk, this.m_btnCancel]) {
      if ((rec.m_wf & kfCtlVisible) === 0) continue;
      if (rec.m_ptbmUp === null || rec.m_ptbmDown === null) continue;
      const pressed = this.m_idcCapture === rec.idc && this.m_fPenInside;
      const ptbm = pressed ? rec.m_ptbmDown : rec.m_ptbmUp;
      ptbm.BltTo(pbm, rec.m_rc.left + this.m_rc.left, rec.m_rc.top + this.m_rc.top, ksideNeutral);
    }
  }

  // ── PaintLayer scaffolding (the FormMgr engine reads these off m_apfrm) ──────
  // The placement form paints via OnPaintSimUI/OnPaintControlsSimUI (called by SimUIForm), NOT
  // the FormMgr paint walk — so the walk hooks are no-ops. Its INPUT rides the FormMgr cook
  // (OnHitTest competes; EventProc → OnPenEvent). Scroll repositions the buttons (OnScroll above).
  EventProc(pevt: Event): boolean {
    switch (pevt.eType) {
      case penDownEvent:
      case penMoveEvent:
      case penUpEvent:
      case penDownEvent2:
      case penMoveEvent2:
      case penUpEvent2:
        return this.OnPenEvent(pevt);
    }
    return false;
  }
  OnUpdateMapInvalidate(_pupd: UpdateMap, _prcOpaque: Rect): void {}
  OnPaintBackground(_pbm: CpuDibBitmap, _pupd: UpdateMap | null): void {}
  OnPaint(_pbm: CpuDibBitmap): void {}
  OnPaintControls(_pbm: CpuDibBitmap, _pupd: UpdateMap | null): void {}
  ScrollInvalidate(_pupd: UpdateMap | null): void {}
  FrameStart(): void {}
  FrameComplete(): void {}
  OnKeyTest(_pevt: Event | null): boolean {
    return false;
  } // not a key target (no kfFrmNoFocus arm)
  BreakCapture(): void {
    this.m_idcCapture = 0;
    this.m_fPenInside = false;
  }
  Dispose(): void {
    if (this.m_pfrmm !== null) this.m_pfrmm.RemoveForm(this);
  }
  GetRect(prc: Rect): void {
    prc.SetRc(this.m_rc);
  }
  SetRect(prc: Rect): void {
    this.InvalidateRect(null);
    this.m_rc.SetRc(prc);
    this.InvalidateRect(null);
  }
  InvalidateRect(prc: Rect | null): void {
    if (this.m_pfrmm === null) return;
    this.m_pfrmm.InvalidateRect(prc ?? this.m_rc);
  }

  /** Damage the whole placement overlay — the hatch outline + the ✓/✗ buttons — in
   *  the playfield update map. SimUIForm.OnUpdateMapInvalidate calls this once per
   *  PAINT while placing: the overlay draws UNGATED every paint (OnPaintSimUI /
   *  OnPaintControlsSimUI), so every painted frame must recomposite the region
   *  (terrain → hatch → FOG over it). Without it, a paint that precedes the
   *  placement OnTimer's invalidation within a tick (kfRedrawBeforeTimer input
   *  paints, kfRedrawMax glide) re-draws the hatch OVER the previous frame's fog —
   *  the idle flashing + the fog-reveal cheat. */
  InvalidatePlacementRects(): void {
    const wpt = { wx: 0, wy: 0 };
    this.ui.deps.sui.GetViewPos(wpt);
    const rcInside = new Rect();
    const rcOutside = new Rect();
    this.GetSubRects(wpt.wx, wpt.wy, rcInside, rcOutside);
    this.InvalidateRect(rcOutside);
    const rcBtn = new Rect();
    for (const rec of [this.m_btnOk, this.m_btnCancel]) {
      rec.GetRect(rcBtn);
      rcBtn.Offset(this.m_rc.left, this.m_rc.top);
      this.InvalidateRect(rcBtn);
    }
  }
}

// canvas-removal R3 (iii): BuildListControl (game/Builder.cpp:916-1066) was
// DELETED. It extended the now-deleted ListControl and was instantiated only by
// the forms.ini ANIMLIST createControl case, which R1/R2 made unreachable — the
// build carousels compute their models directly (the model-builders above).

// game/Builder.cpp:417-426 — BuilderGob::DrawBuildProgress (client paint over
// the SIM builder's build-progress state)
export function BuilderGobDrawBuildProgress(pbldr: BuilderGob, pbm: CpuDibBitmap, prc: Rect): void {
  if (pbldr.m_bq.IsEmpty()) return;

  const clr = GetColor(iclr.kiclrYellow);
  const cxWidth = prc.right - prc.left;
  const nLength = Math.trunc(
    (cxWidth * pbldr.m_fxHealthBuilding) /
      pbldr.ctx.unitConsts[pbldr.m_bq.Peek()].GetArmorStrength(),
  );
  pbm.Fill(prc.left, prc.top, nLength, kcyBuildProgress, clr);
}

//
// MobileUnitBuildForm (game/Builder.cpp:488-914)
//

// game/Builder.cpp:691
const kcupdLimit = 6;

// game/Builder.cpp:690 — int MobileUnitBuildForm::s_iliCurrent = -1
let s_iliCurrent = -1;

// canvas-removal R3 step (i): MobileUnitBuildForm is no longer `extends Form`. It holds the
// buildable list as plain BuildItemRec records (`pv` = ut) + the selection index + the private
// BuildQueue (a sim type — kept) + plain Order/CancelOrder/LimitReached visibility flags, and
// reproduces the EXACT canvas control state machine (Builder.cpp:491-914) so the read-model +
// the kmidBuildOther/kmidAbortBuildOther commands are byte-identical (modal-build.test.ts D-*).
export class MobileUnitBuildForm {
  m_pbldr: BuilderGob | null = null;
  m_bqPrivate = new BuildQueue();
  m_fOrderValid = false;
  m_fLimitReached = false;
  m_cupdLast = 0;
  private readonly ui: BuildUI;
  private items: BuildItemRec[] = [];
  private iliSel = -1; // the selected list index (BuildListControl state), -1 = none
  private orderShown = false; // kidcOrder visibility (UpdateOrderButton)
  private cancelOrderShown = false; // kidcCancelOrder visibility
  // the DOM panel keeps this form "open" (the canvas kfFrmVisible gate on the DefUpdate /
  // OnUnitCompleted host seams) — BuildUI sets/clears it on open/close.
  m_fVisible = false;

  constructor(ui: BuildUI) {
    this.ui = ui;
  }

  // ht.h (MobileUnitBuildForm decl)
  GetOwner(): BuilderGob | null {
    return this.m_pbldr;
  }

  // the currently-selected list record (the canvas GetSelectedItem), or null.
  private selectedItem(): BuildItemRec | null {
    return this.iliSel >= 0 && this.iliSel < this.items.length ? this.items[this.iliSel] : null;
  }
  private selectedUt(): number {
    const it = this.selectedItem();
    return it !== null ? it.pv : -1;
  }

  // game/Builder.cpp:491-586 — populate the buildable-unit list from the sim (no control tree).
  SetOwner(pbldr: BuilderGob): void {
    const ctx = this.ui.deps.ctx;
    this.m_pbldr = pbldr;
    this.m_pbldr.SyncBuildQueue(this.m_bqPrivate);
    this.items = [];
    // forms.ini default visibility: kidcCancelOrder hidden until shown; kidcOrder visible.
    this.cancelOrderShown = false;
    this.orderShown = true;

    // Initialize list with available unit types.
    const pplr = pbldr.GetOwner() as Player;
    const umOwned = ((pplr.GetUnitMask() & kumStructures) | pplr.GetUpgrades()) >>> 0;
    const umAllowed =
      ((pbldr.GetConsts() as BuilderConsts).umCanBuild &
        (ctx.fGodMode ? kumAll : pplr.GetAllowedUnits())) >>>
      0;
    const upgmOwned = pplr.GetUpgradeMask();

    this.m_fOrderValid = !this.m_bqPrivate.IsFull();

    let j = 0;
    for (let i = 0; i < kutMax; i++) {
      const puntc = ctx.unitConsts[i];

      // Does this player have what it takes to build this structure/unit?
      let fDisabled = true;
      if (
        ctx.fGodMode ||
        ((umOwned & puntc.umPrerequisites) >>> 0 === puntc.umPrerequisites &&
          (upgmOwned & puntc.upgmPrerequisites) >>> 0 === puntc.upgmPrerequisites)
      )
        fDisabled = false;

      // Yep, is this one of the types this build form allows?
      if (puntc.um & umAllowed) {
        const panid = this.ui.deps.gctx.anidFor(puntc.szAniName);
        const nStripIcon = panid !== null ? panid.GetStripIndex('icon') : -1;
        if (nStripIcon !== -1) {
          this.items.push({
            pv: puntc.ut,
            panid: panid!,
            nStrip: nStripIcon,
            nFrame: 0,
            fDisabled,
            fSelected: false,
          });
          j++;
          // QUIRK (Builder.cpp:519-531): j is the 1-based add count; the canvas compares it
          // to GetLastSelection() (a 0-based list index) — preserved verbatim.
          if (j === this.m_pbldr.GetLastSelection()) {
            if (fDisabled) this.m_fOrderValid = false;
            // Disable Cancel Order button if nothing queued for that unit.
            this.cancelOrderShown = this.m_bqPrivate.GetUnitCount(puntc.ut) > 0;
          }
        }
      }
    }

    // If nothing added to the list, the order button is invalid.
    if (j === 0) {
      this.m_fOrderValid = false;
      this.cancelOrderShown = false;
    }

    // Select the last selected unit by default (BuildListControl.Select(N)). Select(N, fOnly=
    // false) sets the Nth item iff N is in range (else NO selection); when it selects, it runs
    // OnSelect(knSelUpInside) → OnControlSelected(kidcList) → OnControlNotify(kidcList) — the
    // FULL list-notify body, NOT just UpdateUnitInfo. s_iliCurrent is reset to -1 FIRST, so the
    // notify body's auto-order gate (s_iliCurrent === ili) is false here → it RECORDS the
    // default selection into s_iliCurrent (no order). This is load-bearing: it makes the first
    // tap of the DEFAULT-selected unit order (DomSelect → listNotify finds s_iliCurrent === ili),
    // matching the gold's Select(GetLastSelection()) path.
    s_iliCurrent = -1;
    const sel = this.m_pbldr.GetLastSelection();
    this.iliSel = sel >= 0 && sel < this.items.length ? sel : -1;
    for (let i = 0; i < this.items.length; i++) this.items[i].fSelected = i === this.iliSel;
    if (this.iliSel !== -1) this.listNotify(); // Select → OnControlNotify (records s_iliCurrent)

    // Calc what control needs to be visible.
    if (this.iliSel === -1) {
      s_iliCurrent = -1;
      this.m_fOrderValid = false;
      this.UpdateOrderButton(false);
    } else {
      this.UpdateOrderButton(true);
    }
  }

  // game/Builder.cpp:588-680 — the selected-unit stat block (computed DIRECTLY; returns the
  // SAME strings/meter values the canvas UpdateUnitInfo wrote) + the m_fOrderValid /
  // UpdateOrderButton(false) side effect (Hide the Order button if the item is disabled).
  private unitInfo(pli: BuildItemRec): BuildPanelInfo {
    const ctx = this.ui.deps.ctx;
    const pmuntc = ctx.unitConsts[pli.pv] as MobileUnitConsts;
    const mw = this.ui.meterWidthPx;
    const pw = this.ui.pipWidthPx;

    // Cost
    const cost = String(pmuntc.GetCost());
    const costVal = Math.trunc((pmuntc.GetCost() * 100) / this.ui.GetUnitCostMax());

    // Name
    const name = pmuntc.szLongName.toUpperCase(); // HtStrupr

    // Speed
    const wcOneThirdRange = Math.trunc(
      (ctx.gwcMoveDistPerUpdateMax - ctx.gwcMoveDistPerUpdateMin) / 3,
    );
    let speed = 'MEDIUM';
    if (pmuntc.GetMoveDistPerUpdate() < ctx.gwcMoveDistPerUpdateMin + wcOneThirdRange)
      speed = 'SLOW';
    else if (pmuntc.GetMoveDistPerUpdate() >= ctx.gwcMoveDistPerUpdateMax - wcOneThirdRange)
      speed = 'FAST';
    const speedVal = Math.trunc(
      (pmuntc.GetMoveDistPerUpdate() * 100) / ctx.gwcMoveDistPerUpdateMax,
    );

    // Armor
    const fxOneThirdRange = divfx(
      subfx(ctx.gfxMobileUnitArmorStrengthMax, ctx.gfxMobileUnitArmorStrengthMin),
      itofx(3),
    );
    let armor = 'MEDIUM';
    if (pmuntc.GetArmorStrength() === 0) armor = 'NONE';
    else if (pmuntc.GetArmorStrength() < addfx(ctx.gfxMobileUnitArmorStrengthMin, fxOneThirdRange))
      armor = 'LIGHT';
    else if (pmuntc.GetArmorStrength() >= subfx(ctx.gfxMobileUnitArmorStrengthMax, fxOneThirdRange))
      armor = 'HEAVY';
    const armorVal = Math.trunc(
      (fxtoi(pmuntc.GetArmorStrength()) * 100) / fxtoi(ctx.gfxMobileUnitArmorStrengthMax),
    );

    // Range
    const tcOneThirdRange = Math.trunc(
      ((this.ui.gtcFiringRangeMax - this.ui.gtcFiringRangeMin) * 100) / 3,
    );
    let range = 'MEDIUM';
    if (pmuntc.tcFiringRange === 0) range = 'NONE';
    else if (pmuntc.tcFiringRange * 100 <= tcOneThirdRange) range = 'LOW';
    else if (pmuntc.tcFiringRange * 100 >= this.ui.gtcFiringRangeMax * 100 - tcOneThirdRange)
      range = 'HIGH';
    const rangeVal = Math.trunc(((pmuntc.tcFiringRange - 1) * 100) / this.ui.gtcFiringRangeMax);

    // Description (or the prerequisite list when disabled)
    const description = pli.fDisabled
      ? `This unit requires: ${GetPrerequisiteString(pmuntc, ctx)}.`
      : (pmuntc.szDescription ?? '');

    // Hide the "Order"/"Recruit" button if this unit is disabled.
    this.m_fOrderValid = !this.m_bqPrivate.IsFull() && !pli.fDisabled;
    this.UpdateOrderButton(false);

    return {
      name,
      meterMax: PipMeterCount(mw, 100, pw),
      cost,
      costMeter: PipMeterCount(mw, costVal, pw),
      armor,
      armorMeter: PipMeterCount(mw, armorVal, pw),
      speed,
      speedMeter: PipMeterCount(mw, speedVal, pw),
      range,
      rangeMeter: PipMeterCount(mw, rangeVal, pw),
      damage: {
        infantry: DamagePipCount(pmuntc.nInfantryDamage, pmuntc.ctFiringRate),
        vehicle: DamagePipCount(pmuntc.nVehicleDamage, pmuntc.ctFiringRate),
        structure: DamagePipCount(pmuntc.nStructureDamage, pmuntc.ctFiringRate),
      },
      description,
    };
  }

  // game/Builder.cpp:682-688 — SetLastSelection + clear owner.
  EndForm(): void {
    if (this.m_pbldr !== null) this.m_pbldr.m_iLastSelection = this.iliSel;
    this.m_pbldr = null;
  }

  // game/Builder.cpp:693-794 — the kidcOrder / kidcCancelOrder command arms (the EXACT
  // kmidBuildOther / kmidAbortBuildOther enqueue stays HERE — the view builds NO Message, U1).
  private order(): void {
    const ctx = this.ui.deps.ctx;
    this.m_fOrderValid = !this.m_bqPrivate.IsFull();
    this.UpdateOrderButton(true);

    if (this.m_fOrderValid && !this.m_fLimitReached) {
      const ut = this.selectedUt();
      const msg = newMessage();
      msg.smidSender = ksmidNull;
      msg.mid = kmidBuildOtherCommand;
      msg.BuildOtherCommand.ut = ut;
      msg.smidReceiver = this.m_pbldr!.GetId();
      ctx.cmdq.Enqueue(msg);

      if (this.m_pbldr!.GetOwner() === ctx.plrLocal)
        this.ui.deps.PlaySfx((this.m_pbldr!.GetConsts() as BuilderConsts).sfxUnitBuild);

      // update our private copy of the queue and see if we filled it
      this.m_bqPrivate.Enqueue(ut);
      this.cancelOrderShown = true;

      // Need to update the order button / show the limit reached label.
      this.m_cupdLast = ctx.sim!.GetUpdateCount() - kcupdLimit + 1;
      this.m_fOrderValid = !this.m_bqPrivate.IsFull();
      this.UpdateOrderButton(false);
    }
  }

  private cancelOrder(): void {
    const ctx = this.ui.deps.ctx;
    const ut = this.selectedUt();
    if (this.m_bqPrivate.GetUnitCount(ut) === 0) {
      // Assert(false) — if my input lags this might be possible...
      return;
    }

    const msg = newMessage();
    msg.smidSender = ksmidNull;
    msg.mid = kmidAbortBuildOtherCommand;
    msg.AbortBuildOtherCommand.ut = ut;
    msg.smidReceiver = this.m_pbldr!.GetId();
    ctx.cmdq.Enqueue(msg);

    if (this.m_pbldr!.GetOwner() === ctx.plrLocal)
      this.ui.deps.PlaySfx((this.m_pbldr!.GetConsts() as BuilderConsts).sfxUnitBuildAbort);

    // decrement the queue count. private queue for immediate response
    this.m_bqPrivate.RemoveUnit(ut);

    // Queue is not full by definition but gob count may still be at limit
    this.m_fOrderValid = true;
    this.m_fLimitReached = false;
    const pli = this.selectedItem();
    if (pli !== null) this.m_fOrderValid = !pli.fDisabled;
    this.UpdateOrderButton(false);

    if (this.m_bqPrivate.GetUnitCount(ut) === 0) this.cancelOrderShown = false;
  }

  // game/Builder.cpp:796-833 — the list selection-change / tap notify (→ UpdateUnitInfo +
  // the tap-on-selected auto-order). The C++ runs IDENTICALLY for change vs tap; the
  // distinction emerges from s_iliCurrent (on a CHANGE the new index differs → it records
  // s_iliCurrent; on a re-TAP s_iliCurrent already matches → it orders). nNotify is only the
  // gate this is called under (0 = change, 1 = tap); the body does not branch on it.
  private listNotify(): void {
    const pli = this.selectedItem();
    if (pli === null) {
      s_iliCurrent = -1;
      this.m_fOrderValid = false;
      this.UpdateOrderButton(false);
      return;
    }

    this.unitInfo(pli); // UpdateUnitInfo side effect (info + order button)
    this.cancelOrderShown = this.m_bqPrivate.GetUnitCount(pli.pv) > 0;

    // Can we order?
    this.m_fOrderValid = !this.m_bqPrivate.IsFull() && !pli.fDisabled;
    this.UpdateOrderButton(false);

    if (this.m_fOrderValid && !this.m_fLimitReached) {
      // Tap on a selected item orders it (D-R6).
      const ili = this.iliSel;
      if (s_iliCurrent === ili && !pli.fDisabled) {
        this.order();
      } else {
        s_iliCurrent = ili;
      }
    }
  }

  // game/Builder.cpp:835-855 — Order / LimitReached button visibility (plain flags).
  UpdateOrderButton(fCalcLimit: boolean): void {
    if (fCalcLimit) {
      this.m_fLimitReached = !this.ui.deps.ctx.gobm.IsBelowLimit(
        knLimitMobileUnit,
        this.m_pbldr!.GetOwner() as Player,
      );
    }

    if (this.m_fLimitReached) {
      // kidcLimitReached shown (== limitReached in the model); kidcOrder hidden.
      this.orderShown = false;
    } else {
      this.orderShown = this.m_fOrderValid;
    }
  }

  // game/Builder.cpp:857-882 — the BuildFormDefUpdate host seam (limit re-poll + progress).
  DefUpdate(pbldr: BuilderGob, _fBuildInProgress: boolean): void {
    if (!this.m_fVisible || pbldr !== this.m_pbldr) return;

    // May have multiple builders that caused the limit to be reached. Poll periodically.
    const cupdCurrent = this.ui.deps.ctx.sim!.GetUpdateCount();
    if (Math.abs(cupdCurrent - this.m_cupdLast) > kcupdLimit) {
      this.m_cupdLast = cupdCurrent;
      this.UpdateOrderButton(true);
    }
    // (the canvas plstc.Invalidate() progress-bar repaint is the DOM view's 25ms re-poll.)
  }

  // game/Builder.cpp:893-914 (the BuildFormOnUnitCompleted host seam)
  OnUnitCompleted(pbldr: BuilderGob, ut: number): void {
    if (!this.m_fVisible || pbldr !== this.m_pbldr) return;

    this.m_bqPrivate.RemoveUnit(ut);

    // fix up button state
    const pli = this.selectedItem();
    this.m_fOrderValid = pli === null || !pli.fDisabled;
    this.UpdateOrderButton(true);

    if (this.m_bqPrivate.GetUnitCount(ut) === 0) {
      if (this.selectedUt() === ut) this.cancelOrderShown = false;
    }
  }

  // ── Phase 5 — build/order STEP 3: READ-MODEL + action routers ───────────────
  // The DOM carousel reads its model off THIS form's plain state and routes the user's
  // selection/order back through the SAME order/cancelOrder logic — so the build rules + the
  // EXACT command enqueue stay HERE, not in the view (U1). The form stays SetOwner'd + visible
  // while the panel is up, so DefUpdate / OnUnitCompleted keep refreshing it.

  /** the owning builder (or null when EndForm'd / not SetOwner'd). */
  GetDomOwner(): BuilderGob | null {
    return this.m_pbldr;
  }

  /** build the pure read-model of the carousel at this instant. `heading` is the LABEL=0 baked
   *  title ("ORDER VEHICLE" / "RECRUIT PERSONNEL"), supplied by BuildUI. Computed DIRECTLY from
   *  the sim + this form's plain state — the SAME values the canvas painted. */
  BuildModel(heading: string): BuildPanelModel {
    const ctx = this.ui.deps.ctx;
    const side = ctx.plrLocal!.GetSide();

    const items: BuildPanelItem[] = [];
    for (const it of this.items) {
      const ut = it.pv;
      // the per-item LIVE overlays (DrawItem): the private-queue count + the yellow
      // build-progress bar for the unit currently building.
      const queueCount = this.m_bqPrivate.GetUnitCount(ut);
      let buildProgress: number | null = null;
      if (this.m_pbldr !== null && queueCount > 0 && this.m_pbldr.UnitBuildInProgress() === ut) {
        const armor = ctx.unitConsts[ut].GetArmorStrength();
        buildProgress =
          armor > 0 ? Math.max(0, Math.min(1, this.m_pbldr.m_fxHealthBuilding / armor)) : 0;
      }
      items.push({
        ut,
        icon: ExtractListIcon(it, side),
        disabled: it.fDisabled,
        selected: it.fSelected,
        queueCount,
        buildProgress,
      });
    }

    // the stat block — the SAME strings/meters UpdateUnitInfo set for the selection.
    const pli = this.selectedItem();
    const info: BuildPanelInfo | null = pli !== null ? this.unitInfo(pli) : null;

    return {
      variant: 'unit',
      heading,
      items,
      info,
      limitReached: this.m_fLimitReached,
      orderShown: this.orderShown,
      cancelOrderShown: this.cancelOrderShown,
    };
  }

  /** tap a unit by ut — faithfully mirrors the canvas list tap (ListControl.OnPenEvent2):
   *  single-selection (clear others, set the tapped one); if the selection CHANGED →
   *  the knNotifySelectionChange path (→ UpdateUnitInfo); if it was ALREADY selected →
   *  the knNotifySelectionTap path (→ the tap-on-selected Order, D-R6). */
  DomSelect(ut: number): void {
    let iliSel = -1;
    for (let i = 0; i < this.items.length; i++)
      if (this.items[i].pv === ut) {
        iliSel = i;
        break;
      }
    if (iliSel < 0) return;

    // single-selection (the OnPenEvent2 block): did the selection change?
    for (let i = 0; i < this.items.length; i++) {
      this.items[i].fSelected = i === iliSel;
    }
    this.iliSel = iliSel;
    // change OR tap both run the SAME OnControlNotify body (the auto-order is gated by
    // s_iliCurrent, not by which notify) — exactly like the canvas list tap.
    this.listNotify();
  }

  /** Order one of the selected unit — the SAME OnControlSelected(kidcOrder) the canvas button
   *  press runs (the EXACT kmidBuildOtherCommand enqueue, D-R8). */
  DomOrder(): void {
    this.order();
  }

  /** Cancel one order of the selected unit — the SAME OnControlSelected(kidcCancelOrder) (the
   *  EXACT kmidAbortBuildOtherCommand enqueue, D-R8). */
  DomCancelOrder(): void {
    this.cancelOrder();
  }
}

//
// BuildUI — the bundle of C++ statics + the per-gob virtual menu dispatch
//

export class BuildUI {
  readonly deps: BuildUIDeps;
  /** forms.ini-elimination (Phase 4): the in-code IniReader the in-game build/recruit/
   *  research/place/unit-menu Forms Init against — built from the verbatim forms.ini copy
   *  in build-forms-geometry.ts, NOT the shipped pdb forms.ini (which no longer ships or
   *  loads — canvas-removal R2). So these Forms are no longer shipped-forms.ini Forms; the
   *  parse/scale code path is identical, so their control trees are byte-identical to a
   *  forms.ini load (build-forms-geometry.test.ts, drift-guarded against data/forms.ini.pp). */
  readonly formsGeom: IniReader = new IniReader(kBuildFormSections);
  /** gapuntc[ut]->pfrmMenu (UnitGob::LoadMenu, Unit.cpp:731-748). */
  readonly apfrmMenu: (UnitMenu | null)[] = new Array(kutMax).fill(null);
  /** HqGob::s_pfrmBuild (Headquarters.cpp:32). */
  readonly pfrmBuildStructure: StructureBuildForm;
  /** gpfrmPlace (Headquarters.cpp:33). */
  readonly gpfrmPlace: PlaceStructureForm;
  /** VtsGob::s_pfrmBuild (vts.cpp:10) — MobileUnitBuilderConsts.pfrmBuild. */
  readonly pfrmBuildVehicle: MobileUnitBuildForm | null;
  /** s_pfrmUpgrade (Research.cpp:96) — the research-center modal. */
  readonly pfrmUpgrade: UpgradeForm | null;
  /** HrcGob::s_pfrmBuild (HRC.cpp:10). */
  readonly pfrmBuildInfantry: MobileUnitBuildForm | null;
  /** s_ptbmPlacementGood/Bad (Headquarters.cpp:34-35 + 77-80). */
  s_ptbmPlacementGood: TBitmap | null;
  s_ptbmPlacementBad: TBitmap | null;

  // canvas-removal R3 step (i): the build-form PIPMETER geometry, resolved once for the direct
  // (control-free) PipMeterCount: the scaled meter rect width (all build PIPMETERs share the
  // 71-fc rect → ONE width, PcFromFc(71, gcxFullScreenFormDiv10)) + the static pip.png width.
  readonly meterWidthPx: number;
  readonly pipWidthPx: number;

  // The InitClass stat accumulations the TS sim does not track (UI-only):
  // Unit.cpp:86-87 / Unit.cpp:157-158 / Struct.cpp:37-41 / Struct.cpp:59-61
  gnUnitCostMax = -1;
  gnUnitCostMPMax = -1;
  gtcFiringRangeMin = 127;
  gtcFiringRangeMax = -1;
  gfxStructureArmorStrengthMin = itofx(511);
  gfxStructureArmorStrengthMax = -32; // itofx((unsigned)-1) == fix -32 (int16 wrap)
  gnPowerSupplyMax = -1;

  constructor(deps: BuildUIDeps) {
    this.deps = deps;
    const ctx = deps.ctx;
    const host = deps.mfrmm.host;

    // ---- the InitClass stat accumulations (Unit.cpp/Struct.cpp InitClass) ----
    for (let ut = 0; ut < kutMax; ut++) {
      const puntc = ctx.unitConsts[ut];
      if (puntc === undefined || puntc === null) continue;
      this.gnUnitCostMax = Math.max(this.gnUnitCostMax, puntc.nCost);
      this.gnUnitCostMPMax = Math.max(this.gnUnitCostMPMax, puntc.nCostMP);
      this.gtcFiringRangeMin = Math.min(this.gtcFiringRangeMin, puntc.tcFiringRange);
      this.gtcFiringRangeMax = Math.max(this.gtcFiringRangeMax, puntc.tcFiringRange);
      if (puntc instanceof StructConsts) {
        // Don't include Headquarters in min/max since they aren't player-buildable
        if (puntc.gt !== kgtHeadquarters) {
          this.gfxStructureArmorStrengthMin = Math.min(
            this.gfxStructureArmorStrengthMin,
            puntc.fxArmorStrength,
          );
          this.gfxStructureArmorStrengthMax = Math.max(
            this.gfxStructureArmorStrengthMax,
            puntc.fxArmorStrength,
          );
        }
        this.gnPowerSupplyMax = Math.max(this.gnPowerSupplyMax, puntc.nPowerSupply);
      }
    }

    // (the per-class sfx consts assignments live with each gob's InitClass in
    // sim/src/gob/ — hq.ts/vts.ts/structtypes.ts etc., the C++ home — since
    // the M6 audio wiring; this used to back-patch HQ/VTS/HRC here)

    // canvas-removal R3 step (i): the build/recruit/research carousels + the per-gob menus are
    // no longer Form control-hosts — they are plain model-builder classes (no mfrmm.Init/RemoveForm,
    // no control tree). The placement form is a PaintLayer (Init parses its Ok/Cancel section).

    // resolve the PIPMETER geometry once (all build PIPMETERs share the 71-fc rect → ONE width).
    this.meterWidthPx = PcFromFc(71, host.gcxFullScreenFormDiv10);
    // pip.png glyph WIDTH (the deleted PipMeterControl.s_ptbmPip, loaded directly):
    // the byte-identical pip-count formula needs only its cx (misccontrols.cpp:1748).
    const ptbmPip = host.createTBitmap('pip.png');
    this.pipWidthPx = ptbmPip !== null ? ptbmPip.GetSize().cx : 0;

    // ---- HqGob::InitClass's form preloads (Headquarters.cpp:48-80) ----
    this.pfrmBuildStructure = new StructureBuildForm(this);

    this.gpfrmPlace = new PlaceStructureForm(this);
    if (!this.gpfrmPlace.Init(deps.gpfrmmSim, this.sectionFor(kidfPlaceStructure))) {
      throw new Error('BuildUI: PlaceStructureForm Init failed');
    }

    // Preload the placement tile bitmaps
    this.s_ptbmPlacementGood = host.createTBitmap('placementGood.png');
    this.s_ptbmPlacementBad = host.createTBitmap('placementBad.png');

    // ---- VtsGob/HrcGob::InitClass's form preloads (vts.cpp:27-33, HRC.cpp:29-35)
    this.pfrmBuildVehicle = new MobileUnitBuildForm(this);
    this.pfrmBuildInfantry = new MobileUnitBuildForm(this);

    // ---- ResearchGob::InitClass's form preload (Research.cpp:127-145) ----
    this.pfrmUpgrade = new UpgradeForm(this);

    // ---- UnitGob::LoadMenu per unit type (MobileUnit.cpp:120 kidfUnitMenu /
    // Struct.cpp:73 kidfStructMenu defaults; gobtemplates "Menu" overrides) ----
    for (let ut = 0; ut < kutMax; ut++) {
      const puntc = ctx.unitConsts[ut];
      if (puntc === undefined || puntc === null) continue;
      const idfDefault = puntc instanceof StructConsts ? kidfStructMenu : kidfUnitMenu;
      this.apfrmMenu[ut] = this.LoadMenu(puntc, idfDefault);
    }
  }

  // find the kBuildFormSections section for an idf (the in-code forms.ini copy section).
  private sectionFor(idf: number): import('../../../sim/src/data/ini.ts').IniSection {
    const name = String(idf);
    const s = kBuildFormSections.find((x) => x.name === name);
    if (s === undefined) throw new Error(`BuildUI: no build-form section ${idf}`);
    return s;
  }

  // ht.h:6439 GetUnitCostMax()
  GetUnitCostMax(): number {
    return this.deps.ctx.fMultiplayer ? this.gnUnitCostMPMax : this.gnUnitCostMax;
  }

  // game/Unit.cpp:731-748 — build the per-gob menu (off Form: parse the section's buttons + title
  // into a MenuDef, no control tree). The "Menu" GobTemplates override picks the section idf.
  private LoadMenu(puntc: UnitConsts, idfDefault: number): UnitMenu | null {
    const r = this.deps.templatesIni.GetPropertyValue(String(puntc.gt), 'Menu', '%d');
    const idf = r.count === 1 ? (r.values[0] as number) : idfDefault;
    const name = String(idf);
    const section = kBuildFormSections.find((x) => x.name === name);
    if (section === undefined) return null;
    const def = parseMenuDef(section, this.deps.mfrmm.host);
    return new UnitMenu(this, def);
  }

  /** gpfrmPlace for the SimUIHost.GetPlaceForm seam. */
  GetPlaceForm(): PlaceStructureForm {
    return this.gpfrmPlace;
  }

  // FingerHandler::ShowUnitTitle (fingerhandler.cpp:668-690): build the hover-title flourish for
  // `pgob` — a UnitMenu on the kidfUnitMenu section (title-only, no reloc buttons — the C++ ALWAYS
  // Init's the title with kidfUnitMenu regardless of gob type), SetOwner(pgob, false) (no InitMenu),
  // wrapped in a PaintLayer + AddLayer'd to mfrmm (the sim region) so the FormMgr paint walk renders
  // it. Returns the layer (the FingerHandler holds it + Dispose's it on teardown), or null.
  MakeUnitTitle(pgob: Gob): UnitTitleLayer | null {
    if ((pgob.GetFlags() & kfGobUnit) === 0) return null;
    const section = kBuildFormSections.find((x) => x.name === String(kidfUnitMenu));
    if (section === undefined) return null;
    const menu = new UnitMenu(this, parseMenuDef(section, this.deps.mfrmm.host));
    menu.SetOwner(pgob as UnitGob, false); // fPerUnitInit=false (no InitMenu — the title strip)
    const layer = new UnitTitleLayer(menu);
    this.deps.mfrmm.AddLayer(layer);
    return layer;
  }

  // game/Unit.cpp:707-729 — UnitGob::PopupMenu (the DoModal tail is the
  // continuation; the C++ blocks at :723)
  PopupMenu(pgob: Gob): void {
    if (!(pgob.GetFlags() & kfGobUnit)) return; // Gob::PopupMenu — do-nothing default
    const punt = pgob as UnitGob;
    const pfrmMenu = this.apfrmMenu[punt.GetUnitType()];
    if (pfrmMenu === null) return;

    // UNDONE: show a special being-built menu (Abort only)
    if (punt.GetFlags() & kfGobBeingBuilt) return;

    // canvas-removal R3 step (i): the gob CONTEXT MENU is a DOM overlay (the canvas modal arm
    // is gone — ?dom is the only path). The view is persistent + live-refreshing, so the seam is
    // pull-shaped: getModel() returns the CURRENT read-model (the SAME InitMenu/SetOwner logic)
    // or null when the menu should close (the gob deactivated/deselected); onSelect(idc) routes
    // to the SAME OnMenuItemSelected. The view constructs NO command/Message. Absent (a headless
    // harness, domUnitMenu unset) ⇒ no UI shown (nothing to fall back to).
    if (this.deps.domUnitMenu === undefined) return;
    // a hard guard on the owner gob: getModel returns null once the gob is no longer active.
    const getModel = (): UnitMenuModel | null => {
      if (!(punt.GetFlags() & kfGobActive)) return null;
      return this.GetMenuModel(punt);
    };
    const onSelect = (idc: number): void => {
      this.OnMenuItemSelected(punt, idc);
    };
    // getModel returning null tears the overlay down on the view's next poll (the CloseUnitMenu
    // equivalent — Unit.cpp:399-403); nothing to do on close.
    const onClose = (): void => {
      /* the host unmounts; nothing to do here */
    };
    this.deps.domUnitMenu(getModel, onSelect, onClose);
  }

  // Phase 5 — STEP 2: build a pure read-model of the gob CONTEXT MENU for
  // `punt` at this instant. Reuses the EXACT canvas logic — runs InitMenu against the
  // SAME preloaded UnitMenu form (so the visible/enabled buttons match the canvas
  // pixel-for-pixel), reads the controls back, and computes the gob's on-screen rect
  // via the SetOwner geometry pipeline (in GAME px; the host scales by kGameScale).
  // The button art is the control's OWN TBitmaps (the canvas PNGs, recolored kside1).
  // DISPLAY-only: no command/Message, no sim mutation.
  GetMenuModel(punt: UnitGob): UnitMenuModel | null {
    const pfrm = this.apfrmMenu[punt.GetUnitType()];
    if (pfrm === null) return null;

    // run the EXACT per-gob-type Show/Enable logic the canvas runs on its 25ms timer
    // (Unit.cpp:1095-1101 OnTimer → InitMenu). This mutates only the (host-side) form
    // control flags, never the sim.
    this.InitMenu(punt, pfrm);

    // read the reloc-buttons back IN FORM ORDER (the records are already only the reloc buttons,
    // in form order). A button is in the model iff it is VISIBLE. Bitmap buttons (Build/Repair/
    // Sell/…) carry their PNG art; the Transform/Deliver TEXT buttons (a label, no PNG) get no art.
    const buttons: UnitMenuButton[] = [];
    for (const rec of pfrm.buttons) {
      const wf = rec.GetFlags();
      if (!(wf & kfCtlVisible)) continue;
      buttons.push({
        idc: rec.idc,
        label: kMenuButtonLabel.get(rec.idc) ?? '',
        enabled: (wf & kfCtlDisabled) === 0,
        art: this.ExtractButtonArt(rec) ?? kTextButtonArt,
      });
    }

    // kidfMobileHqMenu's "Can't Transform…" label (kidcCantTransform 1201) — shown
    // when Transform is hidden (MobileHQ.cpp:109-116).
    let cantTransform: string | null = null;
    const recCt = pfrm.cantTransformRec;
    if (recCt !== null && recCt.m_wf & kfCtlVisible) {
      cantTransform = recCt.text;
    }

    return {
      title: punt.GetConsts().szName,
      // WHITE — the canvas kidcTitle LabelControl sets NO text colour (SetOwner does no
      // SetTextColor), so the kifntTitle font draws its default white, NOT the side colour.
      // (Only the confirm DialogForm's title BAR is side-coloured — a fill, not this text.)
      titleColor: cssColor(GetColor(iclr.kiclrWhite)),
      buttons,
      cantTransform,
      anchor: this.GobScreenRect(punt),
    };
  }

  // The gob's on-screen rect in GAME px — the SAME pipeline UnitMenu.SetOwner uses
  // (Unit.cpp:1107-1283, buildui.ts:450-463): rcUIBounds from the gob's wrcUIBounds
  // + GetPosition, offset by the even-snapped view origin (matching the renderer's
  // gobdraw/simdraw `PcFromUwc(view) & 0xfffe`).
  private GobScreenRect(punt: UnitGob): {
    x: number;
    y: number;
    w: number;
    h: number;
    titleTop: number;
  } {
    const c = this.deps.gctx.coords;
    const wpt = { wx: 0, wy: 0 };
    this.deps.sui.GetViewPos(wpt);
    const xViewOrigin = c.PcFromUwc(wpt.wx) & 0xfffe;
    const yViewOrigin = c.PcFromUwc(wpt.wy) & 0xfffe;

    const b = punt.GetConsts().wrcUIBounds;
    const left = c.PcFromWc(b.left);
    const top = c.PcFromWc(b.top);
    const right = c.PcFromWc(b.right);
    const bottom = c.PcFromWc(b.bottom);

    const wptPos = { wx: 0, wy: 0 };
    punt.GetPosition(wptPos);
    const xPos = c.PcFromWc(wptPos.wx) - xViewOrigin;
    const yPos = c.PcFromWc(wptPos.wy) - yViewOrigin;

    // the TITLE anchor: above the hilight SPRITE when present (SetOwner:571-578), else the
    // UIBounds top. GetAnimSpriteBounds returns screen px (gob-relative), offset by yPos
    // like SetOwner does (it does NOT PcFromWc the sprite rect).
    const rcSprite = this.deps.GetAnimSpriteBounds(punt);
    const titleTop = rcSprite !== null ? rcSprite.top + yPos : top + yPos;
    return { x: left + xPos, y: top + yPos, w: right - left, h: bottom - top, titleTop };
  }

  // Extract a bitmap button's art (up/down + disabled) from its OWN TBitmaps — the
  // EXACT canvas PNGs (build_btn/repair_btn/abort/sell), recolored to the side-1 team
  // colors (the menu forces kfCtlUseSide1Colors). null when the record has no up/down
  // bitmaps (the Transform/Deliver TEXT buttons).
  private ExtractButtonArt(rec: MenuButtonRec): UnitMenuButtonArt | null {
    const up = tbmToRgba(rec.m_ptbmUp);
    const down = tbmToRgba(rec.m_ptbmDown);
    if (up === null || down === null) return null;
    return { up, down, disabled: tbmToRgba(rec.m_ptbmDisabled) };
  }

  // Unit.cpp:399-403 (the CloseUnitMenu host seam) — the sim's gob-Deactivate hook. The
  // context menu is a DOM overlay now (it tears down when getModel() returns null on the gob's
  // deactivation, which the seam already gates on kfGobActive), so there is no canvas modal to
  // EndForm; clear the shared menu's owner reference if it is this gob (parity bookkeeping).
  CloseUnitMenu(pgob: Gob): void {
    if (!(pgob.GetFlags() & kfGobUnit)) return;
    const punt = pgob as UnitGob;
    const pfrmMenu = this.apfrmMenu[punt.GetUnitType()];
    if (pfrmMenu !== null && pfrmMenu.GetOwner() === punt) pfrmMenu.m_punt = null;
  }

  // Phase 5 — build/order STEP 3: the UNIT BUILD carousel form currently routed to
  // the DOM overlay (null when none open). Tracked so wiUi/the host can read its live model
  // and the game-over teardown can close it.
  pfrmDomBuildPanel: MobileUnitBuildForm | null = null;

  // open the VTS/HRC build carousel as a DOM overlay (the domBuildPanel seam). SetOwner
  // runs the canvas list-population + UpdateOrderButton; kfFrmVisible keeps the live host
  // seams (DefUpdate/OnUnitCompleted) refreshing it. The view pulls a read-model and routes
  // selection/order back through the form — the command enqueue stays here (U1).
  private OpenBuildPanelDom(punt: MobileUnitBuilderGob, pfrmBuild: MobileUnitBuildForm): void {
    // close any previously-open DOM panel first (a fresh Build replaces it).
    if (this.pfrmDomBuildPanel !== null && this.pfrmDomBuildPanel !== pfrmBuild) {
      this.CloseBuildPanelDom();
    }
    // mark visible so BuildFormDefUpdate / BuildFormOnUnitCompleted (which gate on
    // m_fVisible) keep firing; SetOwner populates the list + private queue.
    pfrmBuild.m_fVisible = true;
    pfrmBuild.SetOwner(punt);
    this.pfrmDomBuildPanel = pfrmBuild;
    this.deps.PlaySfx(ksfxGuiFormShow);

    const isVts = punt.GetUnitType() === kutVehicleTransportStation;
    const heading = isVts ? 'ORDER VEHICLE' : 'RECRUIT PERSONNEL';
    // the help section anchor — Builder.cpp:703 Help(m_idf == kidfBuildInfantry ? "personnel"
    // : "vehicles"): the VTS builds vehicles, the HRC recruits personnel.
    const helpAnchor = isVts ? 'vehicles' : 'personnel';

    // getModel: re-poll the LIVE form. null once the builder deactivated (the canvas
    // BuilderDeactivate would EndForm; here getModel→null tears the overlay down).
    const getModel = (): BuildPanelModel | null => {
      if (pfrmBuild.GetDomOwner() !== punt || !(punt.GetFlags() & kfGobActive)) return null;
      return pfrmBuild.BuildModel(heading);
    };
    const onSelect = (ut: number): void => {
      pfrmBuild.DomSelect(ut);
    };
    const onOrder = (): void => {
      pfrmBuild.DomOrder();
    };
    const onCancelOrder = (): void => {
      pfrmBuild.DomCancelOrder();
    };
    const onClose = (): void => {
      this.CloseBuildPanelDom();
    };
    // the "?" opens the section-only DOM help OVER this panel (the panel stays open).
    const onHelp = (): void => {
      this.deps.domHelp?.(helpAnchor);
    };
    this.deps.domBuildPanel!(getModel, onSelect, onOrder, onCancelOrder, onClose, onHelp);
  }

  // close the DOM build panel: EndForm (GetLastSelection persistence — D-R7) + clear the
  // visible flag we set so the host seams stop refreshing it.
  CloseBuildPanelDom(): void {
    const pfrm = this.pfrmDomBuildPanel;
    if (pfrm === null) return;
    this.pfrmDomBuildPanel = null;
    if (pfrm.GetDomOwner() !== null) pfrm.EndForm(); // saves m_iLastSelection
    pfrm.m_fVisible = false;
    this.deps.PlaySfx(ksfxGuiFormHide);
  }

  // FORM C: the STRUCTURE BUILD carousel currently routed to the DOM overlay (null
  // when none open). Tracked so wiUi/the host can read its live model + the game-over
  // teardown can close it. (Distinct from the unit pfrmDomBuildPanel — both can never be
  // open at once: each is a different gob's menu, and opening one closes the prior overlay.)
  pfrmDomStructurePanel: StructureBuildForm | null = null;

  // open the HQ STRUCTURE build carousel as a DOM overlay (the domStructurePanel seam).
  // SetOwner runs the canvas list-population + limit check; the view pulls a read-model and
  // routes selection/Build back through the form. "Build" RETURNS A GOB TYPE — the BuildUI
  // continuation (OpenStructurePanelDom's onBuild) runs the gpfrmPlace placement handoff
  // (C-R10); the form ENQUEUES NOTHING (C-R11/U1).
  private OpenStructurePanelDom(punt: HqGob): void {
    // close any previously-open DOM panel first (a fresh Build replaces it).
    this.CloseStructurePanelDom();
    const pfrmBuild = this.pfrmBuildStructure;
    // populate the list/limit via SetOwner (the structure carousel has no host-seam refresh).
    pfrmBuild.SetOwner(punt);
    this.pfrmDomStructurePanel = pfrmBuild;
    this.deps.PlaySfx(ksfxGuiFormShow);

    const heading = 'ORDER BUILDING';

    // getModel: re-poll the LIVE form. null once the HQ deactivated (→ tear the overlay down).
    const getModel = (): BuildPanelModel | null => {
      if (pfrmBuild.GetDomOwner() !== punt || !(punt.GetFlags() & kfGobActive)) return null;
      return pfrmBuild.BuildModel(heading);
    };
    const onSelect = (ut: number): void => {
      pfrmBuild.DomSelect(ut);
    };
    // "Build" → end the form with the selected structure's gt + run the placement handoff.
    const onBuild = (): void => {
      const gt = pfrmBuild.DomBuild(); // EndForm() (saves m_iLastSelection); NO enqueue
      this.pfrmDomStructurePanel = null; // the form ended — clear our tracking
      this.deps.PlaySfx(ksfxGuiFormHide);
      if (gt === null) return; // nothing selected → nothing to place
      // the EXACT canvas HQ continuation (Headquarters.cpp:139-147, buildui.ts:2993-3000):
      // hand gt → gpfrmPlace, then the CANVAS placement overlay (FORM F) emits the command.
      this.gpfrmPlace.SetOwner(punt, SimGetUnitConsts(this.deps.ctx, gt)!.ut);
      punt.m_unvl.MinSkip();
      this.gpfrmPlace.OnDisplayResize();
      this.gpfrmPlace.m_wf |= kfFrmVisible; // a layer must be visible for OnHitTest + the paint walk
      this.deps.gpfrmmSim.AddLayer(this.gpfrmPlace); // it's a PaintLayer now (not AddForm)
    };
    const onClose = (): void => {
      this.CloseStructurePanelDom();
    };
    // the "?" opens the section-only DOM help (the BUILDINGS section) OVER this panel.
    const onHelp = (): void => {
      this.deps.domHelp?.('buildings');
    };
    this.deps.domStructurePanel!(getModel, onSelect, onBuild, onClose, onHelp);
  }

  // close the DOM structure panel: EndForm (GetLastSelection persistence — C-R9) + clear
  // the visible flag. (Close / backdrop tap → kidcCancel, no placement handoff.)
  CloseStructurePanelDom(): void {
    const pfrm = this.pfrmDomStructurePanel;
    if (pfrm === null) return;
    this.pfrmDomStructurePanel = null;
    if (pfrm.GetDomOwner() !== null) pfrm.EndForm(); // saves m_iLastSelection
    this.deps.PlaySfx(ksfxGuiFormHide);
  }

  // FORM E: the RESEARCH carousel currently routed to the DOM overlay (null when none
  // open). Tracked so wiUi/the host can read its live model + the game-over teardown can
  // close it. (Distinct from the unit/structure panels — only one is ever open at a time:
  // opening any one closes the prior overlay; each is a different gob's menu.)
  pfrmDomUpgradePanel: UpgradeForm | null = null;

  // open the RESEARCH carousel as a DOM overlay (the domUpgradePanel seam). SetOwner runs the
  // canvas list-population (already-owned upgrades omitted, the empty state); the view pulls a
  // read-model and routes selection/Research back through the form. "Research" RETURNS THE upgm
  // — the BuildUI continuation (OpenUpgradePanelDom's onResearch) enqueues kmidUpgradeCommand,
  // the EXACT canvas DoModal-callback enqueue (E-R5); the form ENQUEUES NOTHING (U1).
  private OpenUpgradePanelDom(punt: ResearchGob): void {
    const pfrmUpgrade = this.pfrmUpgrade;
    if (pfrmUpgrade === null) return;
    // close any previously-open DOM panel first (a fresh Research replaces it).
    this.CloseUpgradePanelDom();
    // populate the list via SetOwner (the research carousel has no host-seam refresh).
    pfrmUpgrade.SetOwner(punt);
    this.pfrmDomUpgradePanel = pfrmUpgrade;
    this.deps.PlaySfx(ksfxGuiFormShow);

    const heading = 'R&D TECHNOLOGIES';
    const ctx = this.deps.ctx;

    // getModel: re-poll the LIVE form. null once the research center deactivated (→ tear down).
    const getModel = (): BuildPanelModel | null => {
      if (pfrmUpgrade.GetDomOwner() !== punt || !(punt.GetFlags() & kfGobActive)) return null;
      return pfrmUpgrade.BuildModel(heading);
    };
    const onSelect = (upgm: number): void => {
      pfrmUpgrade.DomSelect(upgm);
    };
    // "Research" → end the form with the selected upgrade's upgm + enqueue kmidUpgradeCommand.
    // This is the EXACT canvas continuation (Research.cpp:209-253, buildui.ts ResearchGob arm):
    // the command is built HERE (U1 — the view builds NO Message), with the wfUpgrade per choice.
    const onResearch = (): void => {
      const upgm = pfrmUpgrade.DomResearch(); // EndForm(); NO enqueue from the form
      this.pfrmDomUpgradePanel = null; // the form ended — clear our tracking
      this.deps.PlaySfx(ksfxGuiFormHide);
      if (upgm === null) return; // nothing selected → nothing to research
      // this must be enqueued because it begins the consumption of credits
      const msg = newMessage();
      msg.mid = kmidUpgradeCommand;
      msg.smidReceiver = punt.GetId();
      msg.smidSender = ksmidNull;
      msg.UpgradeCommand.wfUpgrade = upgm === kupgmAdvancedHRC ? kfUpgradeHrc : kfUpgradeVts;
      ctx.cmdq.Enqueue(msg);
      if (punt.GetOwner() === ctx.plrLocal)
        this.deps.PlaySfx((punt.GetConsts() as StructConsts).sfxUpgrade);
    };
    const onClose = (): void => {
      this.CloseUpgradePanelDom();
    };
    // the "?" opens the section-only DOM help (the Research Upgrades section) OVER this panel.
    const onHelp = (): void => {
      this.deps.domHelp?.('upgrades');
    };
    this.deps.domUpgradePanel!(getModel, onSelect, onResearch, onClose, onHelp);
  }

  // close the DOM research panel: EndForm + clear the visible flag. (Close / backdrop tap →
  // kidcCancel, no command.)
  CloseUpgradePanelDom(): void {
    const pfrm = this.pfrmDomUpgradePanel;
    if (pfrm === null) return;
    this.pfrmDomUpgradePanel = null;
    if (pfrm.GetDomOwner() !== null) pfrm.EndForm();
    this.deps.PlaySfx(ksfxGuiFormHide);
  }

  // The C++ virtual InitMenu dispatch (most-derived class first). Mutates the menu's plain
  // button records (Show/Enable on MenuButtonRec) exactly as the canvas mutated the ButtonControls.
  InitMenu(punt: UnitGob, pfrm: UnitMenu): void {
    if (punt instanceof HqGob) {
      // game/Headquarters.cpp:123-134 — toggle on/off the right stuff
      pfrm.GetButton(kidcBuild)!.Show(!punt.m_bq.IsFull());
      pfrm.GetButton(kidcAbortBuild)!.Show(!punt.m_bq.IsEmpty());
      this.StructInitMenu(punt, pfrm); // BuilderGob::InitMenu == StructGob::InitMenu
      return;
    }
    if (punt instanceof MobileUnitBuilderGob) {
      // game/MobileBuilder.cpp:38-44
      const pmubc =
        punt.GetConsts() as import('../../../sim/src/gob/unitconsts.ts').MobileUnitBuilderConsts;
      pfrm
        .GetButton(kidcBuild)!
        .Enable(
          ((punt.GetOwner() as Player).GetUpgrades() & pmubc.fUpgradeInProgress) !==
            pmubc.fUpgradeInProgress,
        );
      this.StructInitMenu(punt, pfrm); // BuilderGob::InitMenu
      return;
    }
    if (punt instanceof MinerGob) {
      // game/Miner.cpp:168-172 (no base call)
      pfrm.GetButton(kidcDeliver)!.Show(punt.GetGalaxiteAmount() !== 0);
      return;
    }
    if (punt instanceof MobileHqGob) {
      // game/MobileHQ.cpp:109-116 (no base call)
      const pbtn = pfrm.GetButton(kidcTransform)!;
      const plbl = pfrm.cantTransformRec; // the kidcCantTransform LABEL record
      const fCanTransform = punt.CanTransform(this.deps.ctx.fogm!).can;
      pbtn.Show(fCanTransform);
      if (plbl !== null) {
        if (!fCanTransform) plbl.m_wf |= kfCtlVisible;
        else plbl.m_wf &= ~kfCtlVisible;
      }
      return;
    }
    if (punt instanceof ResearchGob) {
      // game/Research.cpp:196-207 — RESEARCH vs ABORT UPGRADE toggle
      const fUpgradeInProgress =
        ((punt.GetOwner() as Player).GetUpgrades() &
          (kfUpgradeHrcInProgress | kfUpgradeVtsInProgress)) >
        0;
      pfrm.GetButton(kidcResearch)!.Show(!fUpgradeInProgress);
      pfrm.GetButton(kidcAbortUpgrade)!.Show(fUpgradeInProgress);
      this.StructInitMenu(punt, pfrm);
      return;
    }
    if (punt instanceof StructGob) {
      this.StructInitMenu(punt, pfrm);
      return;
    }
    // Gob::InitMenu (GameObjects.cpp:516-518) — do-nothing default
  }

  // game/Struct.cpp:797-805
  private StructInitMenu(pstru: StructGob, pfrm: UnitMenu): void {
    const pbtn = pfrm.GetButton(kidcRepair)!;
    pbtn.Enable(pstru.NeedsRepair());
    pbtn.Show((pstru.m_wfUnit & kfUnitRepairing) === 0);

    const pbtnAbort = pfrm.GetButton(kidcAbortRepair)!;
    pbtnAbort.Show((pstru.m_wfUnit & kfUnitRepairing) !== 0);
  }

  // The C++ virtual OnMenuItemSelected dispatch:
  OnMenuItemSelected(punt: UnitGob, idc: number): void {
    const ctx = this.deps.ctx;
    if (punt instanceof HqGob) {
      // game/Headquarters.cpp:136-173
      switch (idc) {
        case kidcBuild:
          // canvas-removal R3 step (i): the STRUCTURE carousel is a DOM overlay (the canvas
          // modal arm is gone — ?dom is the only path). The view pulls the read-model + routes
          // selection/Build back through the form; "Build" RETURNS A GOB TYPE and
          // OpenStructurePanelDom's onBuild runs the gpfrmPlace placement handoff (the command is
          // emitted by the placement overlay later — C-R10/C-R11/U1). Absent ⇒ no UI shown.
          if (this.deps.domStructurePanel !== undefined) this.OpenStructurePanelDom(punt);
          break;

        case kidcAbortBuild:
          this.PopupConfirmation(punt, 'ABORT BUILD', (fOk: boolean) => {
            if (fOk) {
              ctx.cmdq.EnqueueIds(kmidAbortBuildOtherCommand, punt.GetId());
              if (punt.GetOwner() === ctx.plrLocal)
                this.deps.PlaySfx((punt.GetConsts() as BuilderConsts).sfxUnitBuildAbort);
            }
          });
          break;

        default:
          this.StructOnMenuItemSelected(punt, idc); // BuilderGob::OnMenuItemSelected
          break;
      }
      return;
    }
    if (punt instanceof MobileUnitBuilderGob) {
      // game/MobileBuilder.cpp:46-60
      switch (idc) {
        case kidcBuild: {
          // ((MobileUnitBuilderConsts *)m_puntc)->pfrmBuild — kidfBuildVehicle
          // for the VTS, kidfBuildInfantry for the HRC
          const pfrmBuild =
            punt.GetUnitType() === kutVehicleTransportStation
              ? this.pfrmBuildVehicle
              : this.pfrmBuildInfantry;
          if (pfrmBuild === null) break;

          // canvas-removal R3 step (i): the UNIT BUILD carousel is a DOM overlay (the canvas
          // modal arm is gone — ?dom is the only path). SetOwner populates the list/private queue;
          // marking the form visible keeps the BuildFormDefUpdate / BuildFormOnUnitCompleted /
          // BuilderDeactivate host seams firing (the live queue/progress/limit refresh — D-R5).
          // The view pulls the read-model + routes selection/order back through the form (the
          // command enqueue stays here, U1). Absent (a headless harness) ⇒ no UI shown.
          if (this.deps.domBuildPanel !== undefined) this.OpenBuildPanelDom(punt, pfrmBuild);
          break;
        }

        default:
          this.StructOnMenuItemSelected(punt, idc); // BuilderGob::OnMenuItemSelected
          break;
      }
      return;
    }
    if (punt instanceof MinerGob) {
      // game/Miner.cpp:174-195 (no base fallthrough)
      switch (idc) {
        case kidcDeliver: {
          // The player shouldn't have been able to select this command unless
          // it can be carried out.

          // Assert(GetGalaxiteAmount() != 0)

          // (the C++ Message here is stack-allocated WITHOUT memset — only the
          // fields below are meaningful; newMessage zeroes the rest)
          const msg = newMessage();
          msg.mid = kmidDeliverCommand;
          msg.smidSender = punt.GetId();
          msg.smidReceiver = punt.GetId();
          msg.DeliverCommand.gidTarget = kgidNull;
          msg.tDelivery = 0;
          ctx.cmdq.Enqueue(msg);
          this.deps.PlaySfx(ksfxGalaxMinerDeliver);
          break;
        }
      }
      return;
    }
    if (punt instanceof MobileHqGob) {
      // game/MobileHQ.cpp:118-136 (no base fallthrough)
      switch (idc) {
        case kidcTransform:
          // Assert(CanTransform())
          ctx.cmdq.EnqueueIds(kmidTransformCommand, punt.GetId());
          break;

        case kidcSelfDestruct:
          ctx.cmdq.EnqueueIds(kmidSelfDestructCommand, punt.GetId());
          break;
      }
      return;
    }
    if (punt instanceof ResearchGob) {
      // game/Research.cpp:209-253
      switch (idc) {
        case kidcResearch:
          // canvas-removal R3 step (i): the RESEARCH carousel is a DOM overlay (the canvas
          // modal arm is gone — ?dom is the only path). SetOwner populates the list; the view
          // pulls the read-model + routes selection/Research back through the form. "Research"
          // RETURNS THE upgm and OpenUpgradePanelDom's onResearch enqueues kmidUpgradeCommand
          // (E-R5/U1). Absent (a headless harness) ⇒ no UI shown.
          if (this.pfrmUpgrade !== null && this.deps.domUpgradePanel !== undefined)
            this.OpenUpgradePanelDom(punt);
          break;

        case kidcAbortUpgrade:
          // terminate the consumption of credits and restore
          ctx.cmdq.EnqueueIds(kmidAbortUpgradeCommand, punt.GetId());
          if (punt.GetOwner() === ctx.plrLocal)
            this.deps.PlaySfx((punt.GetConsts() as StructConsts).sfxAbortUpgrade);
          break;

        default:
          this.StructOnMenuItemSelected(punt, idc);
          break;
      }
      return;
    }
    if (punt instanceof StructGob) {
      this.StructOnMenuItemSelected(punt, idc);
      return;
    }
    // Gob::OnMenuItemSelected (GameObjects.cpp:520-522) — do-nothing default
  }

  // game/Struct.cpp:807-825
  private StructOnMenuItemSelected(pstru: StructGob, idc: number): void {
    const ctx = this.deps.ctx;
    switch (idc) {
      // RepairCommand acts as a toggle

      case kidcRepair:
      case kidcAbortRepair:
        ctx.cmdq.EnqueueIds(kmidRepairCommand, pstru.GetId());
        break;

      case kidcSelfDestruct:
        this.PopupConfirmation(pstru, 'SELL BUILDING', (fOk: boolean) => {
          if (fOk) ctx.cmdq.EnqueueIds(kmidSelfDestructCommand, pstru.GetId());
        });
        break;
    }
  }

  // game/Struct.cpp:264-340 — StructGob::PopupConfirmation; the C++ returns
  // `idc == kidcOk` synchronously, here through the continuation.
  // canvas-removal R2: the canvas kidfAreYouSure DialogForm + DoModal arm (and its
  // LoadConfirmation/TakedownConfirmation helpers) were deleted — the confirmation is a DOM
  // overlay (env.domConfirm → confirm-view.ts). The result feeds the SAME pfnDone continuation
  // the canvas DoModal did (the caller enqueues the command on fOk; the view constructs no
  // command/Message). Absent (a headless harness, domConfirm unset) ⇒ no UI shown, pfnDone(false).
  PopupConfirmation(pstru: StructGob, pszTitle: string, pfnDone: (fOk: boolean) => void): void {
    if (this.deps.domConfirm === undefined) {
      pfnDone(false);
      return;
    }
    const clr = GetSideColor(pstru.GetSide());
    // the gob's screen CENTRE in game (back-buffer) px — the canvas centred the dialog here
    // (PcFromWc(gobCenter - viewPos)); the DOM view positions over the gob too (× kGameScale).
    // Same as the canvas: NOT screen-centred.
    const cc = this.deps.gctx.coords;
    const wc = { wx: 0, wy: 0 };
    pstru.GetCenter(wc);
    const wv = { wx: 0, wy: 0 };
    this.deps.sui.GetViewPos(wv);
    const anchor = { cx: cc.PcFromWc(wc.wx - wv.wx), cy: cc.PcFromWc(wc.wy - wv.wy) };
    // pfnDone IS the canvas continuation (the caller enqueues the command on fOk) —
    // hand it straight through; the DOM view constructs no command/Message.
    this.deps.domConfirm(pszTitle, `rgb(${clr.r}, ${clr.g}, ${clr.b})`, anchor, pfnDone);
  }

  // The BuilderDeactivate host seam (Headquarters.cpp:178-186 — HqGob;
  // MobileBuilder.cpp:70-77 — MobileUnitBuilderGob)
  BuilderDeactivate(pgob: Gob): void {
    if (pgob instanceof HqGob) {
      if (this.gpfrmPlace.GetOwner() === pgob) this.gpfrmPlace.OnControlSelected(kidcCancel);
      if (this.pfrmBuildStructure.GetOwner() === pgob) this.pfrmBuildStructure.EndForm();
      return;
    }
    if (pgob instanceof MobileUnitBuilderGob) {
      const pfrmBuild =
        (pgob as MobileUnitBuilderGob).GetUnitType() === kutVehicleTransportStation
          ? this.pfrmBuildVehicle
          : this.pfrmBuildInfantry;
      if (pfrmBuild !== null && pfrmBuild.GetOwner() === pgob) pfrmBuild.EndForm();
    }
  }

  // The BuildFormDefUpdate host seam (MobileBuilder.cpp:205-213)
  BuildFormDefUpdate(pgob: Gob, fBuildInProgress: boolean): void {
    if (!(pgob instanceof MobileUnitBuilderGob)) return;
    const pfrmBuild =
      pgob.GetUnitType() === kutVehicleTransportStation
        ? this.pfrmBuildVehicle
        : this.pfrmBuildInfantry;
    pfrmBuild?.DefUpdate(pgob, fBuildInProgress);
  }

  // The BuildFormOnUnitCompleted host seam (MobileBuilder.cpp:184)
  BuildFormOnUnitCompleted(pgob: Gob, ut: number): void {
    if (!(pgob instanceof MobileUnitBuilderGob)) return;
    const pfrmBuild =
      pgob.GetUnitType() === kutVehicleTransportStation
        ? this.pfrmBuildVehicle
        : this.pfrmBuildInfantry;
    pfrmBuild?.OnUnitCompleted(pgob, ut);
  }
}
